import React, { useState, useEffect, useRef } from 'react';
import {
  Image as ImageIcon, Package, Plus,
  Loader2, Upload, CheckCircle2, Trash2, Pencil,
  ShieldCheck, ChevronRight, AlertCircle, FileText, LayoutList,
  MessageSquare, Send, RotateCcw, Search, X, FileDown, BarChart3, Eye
} from 'lucide-react';

import Papa from 'papaparse';

// --- CLOUDFLARE WORKER API SETUP (replaces Firebase/Firestore) ---
// The admin panel no longer talks to Firebase at all — every read/write
// goes through the same Worker API the bot (server.py) uses, which sits
// in front of the Cloudflare D1 database.
const WORKER_URL = process.env.REACT_APP_WORKER_URL || 'https://indus-footwear-api.broad-moon-4182.workers.dev';
const UPLOAD_ENDPOINT = 'https://indus-chatbot.indusfootwear.com/upload.php';

// Thin fetch wrapper — attaches the API key (obtained via /login and kept
// in localStorage) to every request. Throws on non-2xx so callers can
// just try/catch instead of checking .ok everywhere.
async function apiFetch(path, options = {}) {
  const apiKey = localStorage.getItem('indus_admin_api_key');
  const res = await fetch(`${WORKER_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(apiKey ? { 'X-Api-Key': apiKey } : {}),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Request failed (${res.status})`);
  }
  return res.status === 204 ? null : res.json();
}

const db = true; // kept as a simple truthy flag — components check `db` before rendering data-dependent UI, same pattern as before, no longer a real Firestore handle

// --- BRAND COLORS & MASTERS ---
const COLORS = { primary: '#1d3c45', secondary: '#2db2a5' };

const AUDIENCE_MASTERS = ['Men', 'Women', 'Kids Boy', 'Kids Girl', 'Unisex'];

// ─────────────────────────────────────────────────────────────────────────────
// compressImageToBlob
// Converts ANY image format (WebP, PNG, GIF, JPEG) → compressed JPEG Blob.
// Returns a raw binary Blob — NOT base64 — so we can send via FormData
// with zero encode/decode overhead. ~33% smaller payload than base64.
//
//  maxWidth : 800px  — good for WhatsApp catalogue cards
//  quality  : 0.82   — visually lossless for footwear photos at this size
//  White bg fill     — required because JPEG has no alpha channel
// ─────────────────────────────────────────────────────────────────────────────
const compressImageToBlob = (file, maxWidth = 800, quality = 0.82) => {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      let w = img.width;
      let h = img.height;
      if (w > maxWidth) {
        h = Math.round((h * maxWidth) / w);
        w = maxWidth;
      }

      const canvas = document.createElement('canvas');
      canvas.width  = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');

      // White background — flattens WebP/PNG transparency before JPEG conversion
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(objectUrl);

      // toBlob = async, raw binary, no base64 step at all
      canvas.toBlob(
        (blob) => blob ? resolve(blob) : reject(new Error('canvas.toBlob failed')),
        'image/jpeg',
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Image failed to load for compression'));
    };

    img.src = objectUrl;
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// App — root component
// ─────────────────────────────────────────────────────────────────────────────
export default function App() {
  const [products, setProducts] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginBusy, setLoginBusy] = useState(false);

  useEffect(() => {
    // No auto sign-in — a previously saved API key (from a real login via
    // the form below) is the only thing that restores a session. Nothing
    // is trusted until /login has actually verified email+password once.
    const savedKey = localStorage.getItem('indus_admin_api_key');
    setIsAuthenticated(!!savedKey);
    setAuthChecked(true);
    if (savedKey) setIsLoading(false);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginBusy(true);
    try {
      const res = await fetch(`${WORKER_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail.trim(), password: loginPassword }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        localStorage.setItem('indus_admin_api_key', data.apiKey);
        setIsAuthenticated(true);
        setLoginPassword('');
      } else {
        setLoginError('Incorrect email or password.');
      }
    } catch (err) {
      setLoginError('Could not reach the server. Check your connection and try again.');
    } finally {
      setLoginBusy(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('indus_admin_api_key');
    setIsAuthenticated(false);
    setProducts([]);
    setCategoriesData([]);
  };

  // Replaces the old Firestore onSnapshot listeners — D1/the Worker has
  // no real-time push, so instead: fetch once on login, poll periodically
  // in the background, and explicitly refetch right after any add/edit/
  // delete so the UI updates immediately without waiting for the next
  // poll cycle.
  const [productsLoadError, setProductsLoadError] = useState(null);
  const refetchProducts = async (isRetry = false) => {
    try {
      const data = await apiFetch('/products');
      setProducts(data);
      setProductsLoadError(null);
      setIsLoading(false);
    } catch (err) {
      console.error(`Failed to fetch products${isRetry ? ' (after retry)' : ''}:`, err);
      if (!isRetry) {
        // Most failures at this point are transient (a brief network
        // blip, or the Worker waking up from idle) — wait a moment and
        // try once automatically before bothering the user with an
        // error banner. Only shows the banner if BOTH attempts fail.
        // isLoading deliberately stays true through this wait, so the
        // loading spinner keeps showing instead of a misleading "0"
        // flashing on screen before the retry completes.
        await new Promise(resolve => setTimeout(resolve, 2000));
        return refetchProducts(true);
      }
      // Previously this failed silently — products stayed empty with no
      // indication anything went wrong, making a real fetch failure look
      // identical to "genuinely 0 products in the catalogue." Now it's
      // tracked explicitly so the UI can show a clear retry banner
      // instead of a misleading "(0)" count.
      setProductsLoadError(err.message || 'Failed to load products');
      setIsLoading(false);
    }
  };

  const refetchCategories = async () => {
    try {
      const data = await apiFetch('/categories');
      setCategoriesData(data);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  useEffect(() => {
    if (!db || !isAuthenticated) return;
    refetchProducts();
    refetchCategories();
    // NOTE: deliberately no background polling here — each products
    // fetch re-reads the entire catalogue (1500+ rows). Polling every
    // 30s, if this tab is left open all day, could burn through ~88% of
    // the daily D1 read quota on its own, with zero real bot traffic
    // involved. Data refreshes automatically after every add/edit/
    // delete already; use the manual Refresh button in View Inventory
    // for anything else (e.g. a change made from another browser tab).
  }, [isAuthenticated]);

  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: COLORS.secondary }} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
        <form onSubmit={handleLogin} className="bg-white rounded-xl shadow-md p-8 w-full max-w-sm border-t-4" style={{ borderColor: COLORS.secondary }}>
          <div className="flex flex-col items-center mb-6">
            <img src="/indus-logo.png" alt="Indus Footwear" className="h-12 object-contain mb-2"
              onError={e => { e.target.style.display = 'none'; }} />
            <h1 className="font-extrabold text-2xl tracking-tighter" style={{ color: COLORS.primary }}>
              indus<span style={{ color: COLORS.secondary }}>.</span>
            </h1>
            <p className="text-[10px] font-bold text-gray-500 tracking-widest uppercase mt-1">Admin Portal</p>
          </div>
          <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Email</label>
          <input
            type="email"
            required
            autoFocus
            value={loginEmail}
            onChange={e => setLoginEmail(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-4 text-sm focus:outline-none focus:ring-2"
            style={{ '--tw-ring-color': COLORS.secondary }}
          />
          <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Password</label>
          <input
            type="password"
            required
            value={loginPassword}
            onChange={e => setLoginPassword(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-4 text-sm focus:outline-none focus:ring-2"
            style={{ '--tw-ring-color': COLORS.secondary }}
          />
          {loginError && <p className="text-red-600 text-xs font-semibold mb-4">{loginError}</p>}
          <button
            type="submit"
            disabled={loginBusy}
            className="w-full py-2.5 rounded-lg text-sm font-bold text-white shadow-sm disabled:opacity-60"
            style={{ backgroundColor: COLORS.primary }}
          >
            {loginBusy ? 'Signing in...' : 'Secure Login'}
          </button>
        </form>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: COLORS.secondary }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col font-sans">
      <header className="bg-white shadow-sm px-4 md:px-8 py-3 flex justify-between items-center sticky top-0 z-10 border-b-4" style={{ borderColor: COLORS.secondary }}>
        <div className="flex items-center space-x-3">
          <img src="/indus-logo.png" alt="Indus Footwear" className="h-10 object-contain"
            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
          <div style={{ display: 'none', backgroundColor: COLORS.primary }} className="p-2 rounded-lg items-center justify-center">
            <ShieldCheck className="text-white w-5 h-5" />
          </div>
          <div>
            <h1 className="font-extrabold text-2xl leading-tight tracking-tighter" style={{ color: COLORS.primary }}>
              indus<span style={{ color: COLORS.secondary }}>.</span>
            </h1>
            <p className="text-[10px] font-bold text-gray-500 tracking-widest uppercase">Admin Portal</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center px-4 py-2 rounded-lg text-sm font-bold text-white shadow-sm" style={{ backgroundColor: COLORS.primary }}>
            <ShieldCheck className="w-4 h-4 mr-2" /> Admin Dashboard
          </span>
          <button
            onClick={handleLogout}
            className="px-3 py-2 rounded-lg text-xs font-bold text-gray-600 border border-gray-300 hover:bg-gray-50"
          >
            Log out
          </button>
        </div>
      </header>

      <main className="flex-1 w-full mx-auto flex flex-col overflow-hidden">
        <AdminView products={products} categoriesData={categoriesData} db={db} refetchProducts={refetchProducts} refetchCategories={refetchCategories} productsLoadError={productsLoadError} />
      </main>

      <footer className="py-3 text-center border-t border-gray-200 bg-white">
        <p className="text-[11px] font-bold tracking-widest uppercase" style={{ color: COLORS.primary }}>
          © {new Date().getFullYear()} INDUS FOOTWEAR. All rights reserved.
        </p>
      </footer>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AdminView — tabs, forms, upload logic
// ─────────────────────────────────────────────────────────────────────────────
function AdminView({ products, categoriesData, db, refetchProducts, refetchCategories, productsLoadError }) {
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'add_product';
  });

  // Sidebar nav items are real links (real href="?tab=..."), so the
  // browser's native behavior handles opening in a new tab correctly —
  // Ctrl/Cmd+click or middle-click opens in a new tab (browser default,
  // we don't interfere), while a NORMAL left-click switches the view in
  // THIS tab, same as any ordinary website. Previously every click force-
  // opened a new tab regardless of how it was clicked, which wasn't
  // actually the expected behavior for a plain click.
  const handleNavClick = (e, key) => {
    // Let the browser handle modified clicks/middle-click natively (opens
    // in a new tab on its own, since the href is real) — only intercept
    // a plain left-click to switch in place instead of navigating.
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return;
    e.preventDefault();
    setActiveTab(key);
    window.history.replaceState(null, '', `?tab=${encodeURIComponent(key)}`);
    setSuccessMsg('');
    setErrorMsg('');
  };
  const [editingProductId, setEditingProductId] = useState(null);
  const [dynamicCategories, setDynamicCategories] = useState([]);
  const [dynamicColors, setDynamicColors] = useState([]);

  // Activity log — lazy-loaded only when the tab is actually opened
  // (not on every admin session) to avoid unnecessary Firestore reads.
  const [activityLog, setActivityLog] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityLoaded, setActivityLoaded] = useState(false);
  const [activitySearch, setActivitySearch] = useState('');
  const [activityResultFilter, setActivityResultFilter] = useState('all');
  const [activityViewMode, setActivityViewMode] = useState('customers'); // 'customers' | 'log'

  // ── Reports — exact new products & exact new colors per product,
  // computed by comparing the current catalogue against a SAVED SNAPSHOT
  // (stored in this browser's localStorage). This gives a precise diff —
  // exactly which colors are new on which product — rather than the
  // fuzzy "this product was edited at some point" that updatedAt alone
  // can tell you. No database schema change or Worker redeploy needed;
  // the snapshot lives entirely in the browser.
  //
  // Workflow: check the report → see exactly what's new since your last
  // saved snapshot → click "Save Snapshot as New Baseline" to reset the
  // comparison point to right now, ready for the next reporting period.
  const REPORT_SNAPSHOT_KEY = 'indus_report_snapshot_v1';
  const [reportSnapshot, setReportSnapshot] = useState(() => {
    try {
      const raw = localStorage.getItem(REPORT_SNAPSHOT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  // "Show last N day(s)" filter — narrows the date-wise report (and its
  // download) down to a specific recent window, e.g. the last 2 days,
  // instead of everything since the saved baseline. All day-boundary
  // math is done in IST, matching how dates are displayed everywhere
  // else in this tab.
  const [reportDayRange, setReportDayRange] = useState(2);

  const saveReportSnapshot = () => {
    const snapshot = {
      savedAt: new Date().toISOString(),
      products: products.map(p => ({
        id: p.id,
        name: p.name,
        category: p.category,
        colors: (p.variations || []).map(v => v.color),
      })),
    };
    localStorage.setItem(REPORT_SNAPSHOT_KEY, JSON.stringify(snapshot));
    setReportSnapshot(snapshot);
  };

  const [activityRefreshKey, setActivityRefreshKey] = useState(0);

  // Blocked numbers — fetched on mount and polled periodically (small
  // collection, cheap to refresh), plus refetched immediately after any
  // block/unblock action so the UI reflects it right away.
  const [blockedNumbers, setBlockedNumbers] = useState(new Set());
  const refetchBlockedNumbers = async () => {
    if (!db) return;
    try {
      const phones = await apiFetch('/blocked');
      setBlockedNumbers(new Set(phones));
    } catch (err) {
      console.error('Blocklist fetch failed:', err);
    }
  };
  useEffect(() => {
    if (!db) return;
    refetchBlockedNumbers();
    // No background polling — refetched explicitly after every block/
    // unblock action already; that's sufficient for a single-admin tool.
  }, [db]);

  const toggleBlockNumber = async (phone) => {
    if (!db || !phone) return;
    const isBlocked = blockedNumbers.has(phone);
    const label = isBlocked ? 'unblock' : 'block';
    if (!window.confirm(`${isBlocked ? 'Unblock' : 'Block'} ${phone}? ${isBlocked ? 'They will be able to message the bot again.' : 'The bot will silently stop responding to this number.'}`)) return;
    try {
      if (isBlocked) {
        await apiFetch(`/blocked/${encodeURIComponent(phone)}`, { method: 'DELETE' });
      } else {
        await apiFetch('/blocked', { method: 'POST', body: JSON.stringify({ phone }) });
      }
      await refetchBlockedNumbers();
    } catch (err) {
      console.error(`Failed to ${label} ${phone}:`, err);
      alert(`Couldn't ${label} this number — check console for details.`);
    }
  };

  // Starts the live listener the first time the Activity tab is opened,
  // then keeps it subscribed permanently (survives switching to other
  // tabs and back) — previously it unsubscribed on every tab-away and
  // never resubscribed, which is why the data looked frozen/stale until
  // a full page reload. activityRefreshKey lets the manual Refresh
  // button force a clean re-subscribe on demand too.
  useEffect(() => {
    if (activeTab === 'activity' && !activityLoaded) {
      setActivityLoaded(true);
    }
  }, [activeTab, activityLoaded]);

  useEffect(() => {
    if (!activityLoaded || !db) return;
    setActivityLoading(true);
    apiFetch('/activity?limit=500')
      .then(data => {
        setActivityLog(data);
        setActivityLoading(false);
      })
      .catch(err => {
        console.error('Activity log fetch failed:', err);
        setActivityLoading(false);
      });
  }, [activityLoaded, db, activityRefreshKey]);

  // Product form
  const [productData, setProductData] = useState({ name: '', category: '', audience: '' });
  const [colorsList, setColorsList] = useState([{ color: '', file: null, previewUrl: '', existingUrl: '' }]);

  // Inventory tab — search + multi-category filter + sort
  const [inventorySearch, setInventorySearch] = useState('');
  const [inventoryCategoryFilters, setInventoryCategoryFilters] = useState([]); // multi-select
  const [inventorySort, setInventorySort] = useState('newest'); // 'newest' | 'oldest' | 'name'
  const [inventoryVisibleCount, setInventoryVisibleCount] = useState(60); // renders products in batches, not all 1500+ at once
  useEffect(() => {
    setInventoryVisibleCount(60);
  }, [inventorySearch, inventoryCategoryFilters, inventorySort]);
  const [selectedProductIds, setSelectedProductIds] = useState(new Set());
  const [bulkCategoryValue, setBulkCategoryValue] = useState('');
  const [bulkCategoryUpdating, setBulkCategoryUpdating] = useState(false);
  const [bulkCategoryProgress, setBulkCategoryProgress] = useState(null); // {done, total}

  const toggleProductSelection = (id) => {
    setSelectedProductIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleBulkCategoryUpdate = async () => {
    if (!db || selectedProductIds.size === 0) return;
    if (!bulkCategoryValue) { alert('Choose a category to apply first.'); return; }
    if (!window.confirm(`Move ${selectedProductIds.size} selected product(s) to "${bulkCategoryValue}"?`)) return;

    setBulkCategoryUpdating(true);
    const ids = Array.from(selectedProductIds);
    setBulkCategoryProgress({ done: 0, total: ids.length });

    let done = 0;
    for (const id of ids) {
      try {
        await apiFetch(`/products/${id}`, {
          method: 'PUT',
          body: JSON.stringify({ category: bulkCategoryValue }),
        });
      } catch (err) {
        console.error(`Failed to update category for product ${id}:`, err);
      }
      done++;
      setBulkCategoryProgress({ done, total: ids.length });
    }

    await refetchProducts();
    setBulkCategoryUpdating(false);
    setBulkCategoryProgress(null);
    setSelectedProductIds(new Set());
    setBulkCategoryValue('');
  };

  // Bulk upload (CSV) state
  const [bulkParsedProducts, setBulkParsedProducts] = useState([]); // grouped {name, category, audience, variations:[{color,image}]}[]
  const [bulkParsedRemovedStack, setBulkParsedRemovedStack] = useState([]); // undo history for CSV preview removals
  const [bulkFileName, setBulkFileName] = useState('');
  const [bulkParseErrors, setBulkParseErrors] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null); // {done, total}
  const [bulkResults, setBulkResults] = useState(null); // {added, skipped, failed}

  // Category form
  const [catForm, setCatForm] = useState({ id: null, name: '', pdfLink: '' });

  // Bot Tester state
  const [botEndpoint, setBotEndpoint] = useState('http://localhost:3000');
  const [botAdminKey, setBotAdminKey] = useState('');
  const [testFromNumber, setTestFromNumber] = useState('919999999999'); // Cloud API uses bare E.164, no "whatsapp:" prefix
  const [chatLog, setChatLog] = useState([]); // { sender: 'user'|'bot', text, mediaUrl }[]
  const [testInput, setTestInput] = useState('');
  const [isSendingTest, setIsSendingTest] = useState(false);

  // Refresh the LIVE bot's product cache directly from here — no more
  // needing a terminal/curl for this. Uses the same Bot Server URL +
  // Admin Key fields as Bot Tester (switch the URL to your real Render
  // URL here when you want to refresh the live bot, vs localhost for
  // local testing).
  const [botCacheRefreshing, setBotCacheRefreshing] = useState(false);
  const [botCacheRefreshResult, setBotCacheRefreshResult] = useState(null);
  const refreshBotCache = async () => {
    if (!botEndpoint || !botAdminKey) {
      alert('Set the Bot Server URL and Admin Key first (same fields as Bot Tester).');
      return;
    }
    setBotCacheRefreshing(true);
    setBotCacheRefreshResult(null);
    try {
      const res = await fetch(`${botEndpoint.replace(/\/$/, '')}/refresh-cache`, {
        method: 'POST',
        headers: { 'X-Admin-Key': botAdminKey },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || `Request failed (${res.status})`);
      setBotCacheRefreshResult({ success: true, count: data.products_cached });
    } catch (err) {
      setBotCacheRefreshResult({ success: false, error: err.message });
    } finally {
      setBotCacheRefreshing(false);
    }
  };

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { index, total, pct } | null
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    // Category Master (the "Category Master" tab) is now the single source
    // of truth for which categories can be assigned to a product. Old
    // hardcoded defaults and one-off categories left over on individual
    // products no longer clutter the dropdown — only categories explicitly
    // added in Category Master appear. (If a product's current category
    // was since removed from Category Master, it's still preserved on that
    // product's own record and shown as an option only while editing it —
    // see the category <select> in the product form.)
    const cats = new Set(categoriesData.map(c => c.name));
    setDynamicCategories([...cats].sort());
  }, [categoriesData]);

  useEffect(() => {
    const cols = new Set();
    products.forEach(p => (p.variations || []).forEach(v => { if (v.color) cols.add(v.color); }));
    setDynamicColors([...cols].sort());
  }, [products]);

  // ── Color row handlers ────────────────────────────────────────────────────
  const handleColorImage = (e, index) => {
    const file = e.target.files[0];
    if (!file) return;
    const newColors = [...colorsList];
    newColors[index] = { ...newColors[index], file, previewUrl: URL.createObjectURL(file) };
    setColorsList(newColors);

    // Auto-fill article name + color from the filename, same convention
    // as Bulk Photos: "ARTICLE COLOR.jpg" — everything before the first
    // space is the article name, everything after is the color. Never
    // overwrites something already typed — only fills in blanks, and
    // never touches the name while editing an existing product.
    const base = file.name.replace(/\.[^.]+$/, '').trim();
    const spaceIdx = base.indexOf(' ');
    if (spaceIdx > -1) {
      const articleFromFile = base.slice(0, spaceIdx).trim();
      const colorFromFile = expandColorAbbreviations(base.slice(spaceIdx + 1).trim());

      if (!editingProductId && !productData.name.trim() && articleFromFile) {
        setProductData(prev => ({ ...prev, name: articleFromFile }));
      }
      if (!newColors[index].color.trim() && colorFromFile) {
        const updated = [...newColors];
        updated[index] = { ...updated[index], color: colorFromFile };
        setColorsList(updated);
      }
    }
  };
  const addColor    = () => setColorsList([...colorsList, { color: '', file: null, previewUrl: '', existingUrl: '' }]);
  const removeColor = (idx) => setColorsList(colorsList.filter((_, i) => i !== idx));
  const updateColorName = (idx, val) => {
    const n = [...colorsList]; n[idx].color = val; setColorsList(n);
  };

  // ── UPLOAD — binary FormData (fastest path) ───────────────────────────────
  // Flow:
  //   1. compressImageToBlob  → JPEG Blob (client-side, no base64)
  //   2. FormData.append      → raw binary multipart POST
  //   3. XHR with progress    → live % bar
  //   4. PHP move_uploaded_file → atomic OS rename, no decode work
  //   5. Returns https:// URL → stored in Firestore → Twilio fetches directly
  // ─────────────────────────────────────────────────────────────────────────
  const uploadFile = (file, colorIndex, totalNewFiles) => {
    return new Promise(async (resolve) => {
      try {
        // Step 1 — Compress & convert to JPEG Blob (handles WebP/PNG/GIF/JPEG)
        const jpegBlob = await compressImageToBlob(file);

        // Step 2 — Build multipart FormData with raw binary blob
        const formData = new FormData();
        const safeFilename = file.name.replace(/\.[^.]+$/, '.jpg');
        formData.append('image', jpegBlob, safeFilename);

        // Step 3 — XHR for upload progress tracking
        const xhr = new XMLHttpRequest();
        xhr.open('POST', UPLOAD_ENDPOINT);
        // ⚠ Do NOT set Content-Type — browser sets it automatically with multipart boundary

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploadProgress({
              index: colorIndex + 1,
              total: totalNewFiles,
              pct: Math.round((e.loaded / e.total) * 100)
            });
          }
        };

        xhr.onload = () => {
          try {
            const result = JSON.parse(xhr.responseText);
            if (result.status === 'success' && result.url) {
              console.log(`✅ Color ${colorIndex + 1} uploaded: ${result.url}`);
              resolve(result.url);
            } else {
              console.error(`❌ upload.php error:`, result.message);
              resolve(null); // null = skip variation, don't store broken base64
            }
          } catch (parseErr) {
            console.error('Response parse error:', parseErr, xhr.responseText);
            resolve(null);
          }
        };

        xhr.onerror = () => {
          console.error(`❌ Network error on color ${colorIndex + 1}`);
          resolve(null);
        };

        xhr.send(formData);

      } catch (compressionErr) {
        console.error('Compression error:', compressionErr);
        resolve(null);
      }
    });
  };

  // ── Product submit ────────────────────────────────────────────────────────
  const handleProductSubmit = async (e) => {
    e.preventDefault();
    if (!db) return;

    const trimmedName = productData.name.trim();
    // If this name already exists (and we're not editing that SAME
    // product), don't block — instead treat this as adding colors to
    // that existing product, same smart behavior as Bulk Photos/CSV
    // Upload: only genuinely new colors get added, any color the
    // product already has is silently skipped, never overwritten.
    const existingProduct = products.find(p => p.id !== editingProductId && String(p.name || '').trim().toLowerCase() === trimmedName.toLowerCase());

    const safeColors = colorsList.filter(c => c.color.trim() !== '');
    if (safeColors.length === 0) { alert("Add at least one color with an image."); return; }
    const missingImg = safeColors.find(c => !c.file && !c.existingUrl);
    if (missingImg) { alert(`Upload an image for color "${missingImg.color}".`); return; }

    // In merge mode (existing product, not currently editing it), drop
    // any color already present on that product before uploading —
    // saves the upload entirely for colors that would just be discarded.
    let colorsToProcess = safeColors;
    let skippedExistingCount = 0;
    if (existingProduct) {
      const existingColorNames = new Set((existingProduct.variations || []).map(v => String(v.color).trim().toLowerCase()));
      colorsToProcess = safeColors.filter(c => !existingColorNames.has(c.color.trim().toLowerCase()));
      skippedExistingCount = safeColors.length - colorsToProcess.length;
      if (colorsToProcess.length === 0) {
        alert(`"${trimmedName}" already has all ${safeColors.length} of these color(s) — nothing new to add.`);
        return;
      }
    }

    setIsSubmitting(true);
    setUploadProgress(null);

    try {
      const finalVariations = [];
      const newFiles = colorsToProcess.filter(c => c.file);
      let uploadCount = 0;

      for (const c of colorsToProcess) {
        let imageUrl = c.existingUrl || null;

        if (c.file) {
          imageUrl = await uploadFile(c.file, uploadCount, newFiles.length);
          uploadCount++;
          if (!imageUrl) {
            alert(`Upload failed for color "${c.color}". Check your server and try again.`);
            setIsSubmitting(false);
            setUploadProgress(null);
            return;
          }
        }

        finalVariations.push({ color: c.color, image: imageUrl });
      }

      setUploadProgress(null);

      if (existingProduct) {
        // Merge mode — append only the new colors to the existing product
        const mergedVariations = [...(existingProduct.variations || []), ...finalVariations];
        await apiFetch(`/products/${existingProduct.id}`, {
          method: 'PUT',
          body: JSON.stringify({ variations: mergedVariations }),
        });
        setSuccessMsg(
          `Added ${finalVariations.length} new color(s) to existing product "${trimmedName}"` +
          (skippedExistingCount > 0 ? ` (${skippedExistingCount} color(s) skipped — already existed).` : '.')
        );
        await refetchProducts();
        resetForm();
        setTimeout(() => setSuccessMsg(''), 4000);
        setIsSubmitting(false);
        return;
      }

      const payload = {
        name: trimmedName,
        category: productData.category.trim(),
        audience: productData.audience.trim(),
        articleId: trimmedName,
        variations: finalVariations,
        updatedAt: new Date().toISOString()
      };

      if (editingProductId) {
        await apiFetch(`/products/${editingProductId}`, { method: 'PUT', body: JSON.stringify(payload) });
        setSuccessMsg('Product updated successfully!');
      } else {
        payload.createdAt = new Date().toISOString();
        await apiFetch('/products', { method: 'POST', body: JSON.stringify(payload) });
        setSuccessMsg('Product added successfully!');
      }
      await refetchProducts();

      resetForm();
      setTimeout(() => setSuccessMsg(''), 3000);

    } catch (err) {
      console.error("Submit error:", err);
      alert(`Failed to save product: ${err.message}`);
    } finally {
      setIsSubmitting(false);
      setUploadProgress(null);
    }
  };

  // Common shorthand used in photo filenames — expanded automatically so
  // the stored color name is always the full word customers/staff expect,
  // regardless of how the file was named. Add more abbreviations here as
  // you come across them; matching is whole-word and case-insensitive.
  const COLOR_ABBREVIATIONS = {
    'BLK': 'Black', 'BRN': 'Brown', 'WHT': 'White', 'MHD': 'Mehandi',
    'GRY': 'Grey', 'GRN': 'Green', 'BLU': 'Blue', 'B.GRN': 'B.Green',
    'CHRY': 'Cherry', 'TN': 'Tan', 'MRN': 'Maroon', 'ONG': 'Orange',
    'PNK': 'Pink', 'PRPL': 'Purple', 'YLW': 'Yellow', 'GLD': 'Gold',
    'SLV': 'Silver', 'BG': 'Beige', 'NVY': 'Navy',
  };

  const expandColorAbbreviations = (colorStr) => {
    const words = colorStr
      .split(/\s+/)
      .map(word => {
        const plainKey = word.toUpperCase().replace(/[.,]$/, '');
        return COLOR_ABBREVIATIONS[word.toUpperCase()] || COLOR_ABBREVIATIONS[plainKey] || word;
      });
    // Dual/multi-tone colors (e.g. filename "BLACK WHITE" or "BLK WHT")
    // are joined with "/" rather than a plain space, so they read as one
    // combined color name — "Black/White" — matching how dual-tone shades
    // are conventionally written, instead of looking like two separate words.
    return words.join('/');
  };

  const resetForm = () => {
    setProductData({ name: '', category: '', audience: '' });
    setColorsList([{ color: '', file: null, previewUrl: '', existingUrl: '' }]);
    setEditingProductId(null);
    setUploadProgress(null);
  };

  // Shared core — parses a FileList/array of files and updates state.
  // Used by both the click-to-select input AND drag-and-drop, so both
  // paths go through identical parsing logic.
  const parseAndSetBulkPhotoFiles = (fileList) => {
    const files = Array.from(fileList || []).filter(f => f.type.startsWith('image/'));
    setBulkPhotoResults(null);
    const parsed = files.map(file => {
      const base = file.name.replace(/\.[^.]+$/, '').trim();
      const tokens = base.split(/\s+/).filter(Boolean);
      if (tokens.length < 2) {
        return { file, article: null, color: null, error: `"${file.name}" — expected "ARTICLE COLOR.jpg", no space found` };
      }

      // Detect the same "sub-article mistaken for color" pattern Fix
      // Split Products catches AFTER the fact — but here, catch it at
      // parse time so it never happens in the first place. The rule:
      // article codes (PLUS-02, CARE-02, I-10) always contain a digit;
      // real colors never do. So if the SECOND token contains a digit,
      // it's a sub-article, not a color — fold it into the article name
      // instead ("SWISS PLUS-02 TAN.jpg" -> article "SWISS-PLUS-02",
      // color "Tan"), rather than the old behavior of treating
      // "PLUS-02 TAN" as one garbled dual-tone color.
      let article, rawColor;
      if (tokens.length >= 3 && /\d/.test(tokens[1])) {
        article = `${tokens[0]}-${tokens[1]}`;
        rawColor = tokens.slice(2).join(' ');
      } else {
        article = tokens[0];
        rawColor = tokens.slice(1).join(' ');
      }

      if (!article || !rawColor) {
        return { file, article: null, color: null, error: `"${file.name}" — couldn't read article/color` };
      }
      const color = expandColorAbbreviations(rawColor);
      return { file, article, color, error: null };
    });
    setBulkPhotoFiles(parsed);
  };

  // Click-to-select input just forwards its FileList into the shared parser
  const handleBulkPhotoFileSelect = (e) => {
    parseAndSetBulkPhotoFiles(e.target.files);
  };

  // Drag-and-drop — same shared parser, triggered by dropping files/a
  // folder onto the dropzone instead of clicking to open a file picker.
  const [bulkPhotoDragActive, setBulkPhotoDragActive] = useState(false);
  const handleBulkPhotoDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setBulkPhotoDragActive(true);
  };
  const handleBulkPhotoDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setBulkPhotoDragActive(false);
  };
  const handleBulkPhotoDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setBulkPhotoDragActive(false);
    if (bulkPhotoProcessing) return;
    parseAndSetBulkPhotoFiles(e.dataTransfer.files);
  };

  const handleBulkPhotoUpload = async () => {
    if (!db || bulkPhotoFiles.length === 0) return;
    if (!bulkPhotoCategory) { alert('Please choose a category for this batch before uploading.'); return; }

    const validFiles = bulkPhotoFiles.filter(f => !f.error);
    if (validFiles.length === 0) { alert('No valid files to upload — check filenames match "ARTICLE COLOR.jpg".'); return; }

    setBulkPhotoProcessing(true);
    setBulkPhotoProgress({ done: 0, total: validFiles.length });

    // Group files by article number (case-insensitive) so multiple colors
    // for the same article land as variations on one product.
    const groups = {};
    validFiles.forEach(f => {
      const key = f.article.toLowerCase();
      if (!groups[key]) groups[key] = { article: f.article, items: [] };
      groups[key].items.push(f);
    });

    let created = 0, updated = 0, failed = 0, skippedExisting = 0;
    let doneCount = 0;
    const productsByName = new Map(products.map(p => [String(p.name || '').trim().toLowerCase(), p]));

    for (const key of Object.keys(groups)) {
      const { article, items } = groups[key];
      const existing = productsByName.get(key);

      // If the product already exists, DON'T touch colors it already has
      // — only genuinely new colors get uploaded at all. This means an
      // existing color's photo is never re-uploaded or overwritten by a
      // bulk batch, even if a file with that color name is included;
      // those files are skipped entirely (no wasted upload/bandwidth).
      let itemsToUpload = items;
      if (existing) {
        const existingColorNames = new Set((existing.variations || []).map(v => String(v.color).trim().toLowerCase()));
        itemsToUpload = items.filter(item => !existingColorNames.has(item.color.trim().toLowerCase()));
        skippedExisting += (items.length - itemsToUpload.length);
      }

      if (itemsToUpload.length === 0) {
        // Every color in this group already existed — nothing to do,
        // but still count the skipped files toward overall progress.
        doneCount += items.length;
        setBulkPhotoProgress({ done: doneCount, total: validFiles.length });
        continue;
      }

      try {
        // Upload only the new-color images
        const uploadedVariations = [];
        for (const item of itemsToUpload) {
          const url = await uploadFile(item.file, doneCount, validFiles.length);
          doneCount++;
          setBulkPhotoProgress({ done: doneCount, total: validFiles.length });
          if (!url) {
            failed++;
            continue;
          }
          uploadedVariations.push({ color: item.color, image: url });
        }
        // Account for any files skipped in this group toward the progress bar
        doneCount += (items.length - itemsToUpload.length);
        setBulkPhotoProgress({ done: doneCount, total: validFiles.length });

        if (uploadedVariations.length === 0) continue;

        if (existing) {
          // Append only the new colors — every existing color/photo on
          // this product is left completely as-is, untouched.
          const mergedVariations = [...(existing.variations || []), ...uploadedVariations];
          await apiFetch(`/products/${existing.id}`, {
            method: 'PUT',
            body: JSON.stringify({ variations: mergedVariations }),
          });
          updated++;
        } else {
          await apiFetch('/products', {
            method: 'POST',
            body: JSON.stringify({
              name: article,
              articleId: article,
              category: bulkPhotoCategory,
              audience: bulkPhotoAudience,
              variations: uploadedVariations,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            }),
          });
          created++;
        }
      } catch (err) {
        console.error(`Bulk photo upload failed for article "${article}":`, err);
        failed++;
      }
    }

    await refetchProducts();
    setBulkPhotoResults({ created, updated, failed, skippedExisting });
    setBulkPhotoProcessing(false);
    setBulkPhotoFiles([]);
    setBulkPhotoRemovedStack([]);
  };

  // ── Bulk upload (CSV) ─────────────────────────────────────────────────────
  // Simplified format for fast large-batch entry (built for 1000s of products
  // where photos aren't ready yet): CSV only needs `name` and `colors`
  // (colors separated by comma or semicolon, e.g. "Black; Brown; Grey").
  // Category and audience are picked ONCE above the upload widget and applied
  // to every row in the file — no need to repeat them per row, and no image
  // URLs are required at all. Products land in Firestore with each color
  // variation present but its image left blank; go to Single Upload
  // (or Edit on the Inventory card) afterwards to attach each color's photo
  // whenever it's ready — nothing blocks the bulk step on images being done.
  const [bulkCategory, setBulkCategory] = useState('');
  const [bulkUploadMode, setBulkUploadMode] = useState('new_only'); // 'new_only' | 'update_existing'
  const [bulkAudience, setBulkAudience] = useState('');

  // Bulk Photos (filename-driven) state — files named "<article> <color>.jpg"
  // e.g. "16501 BLACK.jpg", "44107 BLACK RED.jpg" get parsed automatically:
  // article number becomes the product name/articleId, everything after the
  // first space becomes the color. Matches an existing product by name if
  // one exists (adding/updating that color), otherwise creates a new product.
  const [bulkPhotoCategory, setBulkPhotoCategory] = useState('');
  const [bulkPhotoAudience, setBulkPhotoAudience] = useState('');
  const [bulkPhotoFiles, setBulkPhotoFiles] = useState([]); // [{file, article, color, error}]
  const [bulkPhotoRemovedStack, setBulkPhotoRemovedStack] = useState([]); // undo history — arrays of removed file entries, most recent last
  const [bulkPhotoProcessing, setBulkPhotoProcessing] = useState(false);
  const [bulkPhotoProgress, setBulkPhotoProgress] = useState(null); // {done, total}
  const [bulkPhotoResults, setBulkPhotoResults] = useState(null); // {created, updated, failed}

  // Export the current inventory (respecting active search/category filters)
  // as an Excel-compatible spreadsheet, for offline two-step verification —
  // cross-checking what's actually in the database against physical stock
  // or a printed checklist. One row per product; colors and their image
  // status are listed in a single column so it stays readable in Excel
  // without needing a messy one-row-per-color layout.
  const exportInventoryToExcel = (productsToExport) => {
    if (!productsToExport || productsToExport.length === 0) {
      alert('Nothing to export — the current filtered list is empty.');
      return;
    }

    const rows = productsToExport.map(p => {
      const variations = p.variations || [];
      const colorsWithStatus = variations
        .map(v => {
          const hasImage = v.image && v.image.startsWith('http');
          return `${v.color}${hasImage ? '' : ' (NO IMAGE)'}`;
        })
        .join('; ');
      const missingCount = variations.filter(v => !(v.image && v.image.startsWith('http'))).length;

      return {
        'Article / Name': p.name || '',
        'Category': p.category || '',
        'Audience': p.audience || '',
        'Total Colors': variations.length,
        'Colors (image status)': colorsWithStatus,
        'Missing Images': missingCount,
        'Uploaded On': p.createdAt ? new Date(p.createdAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '',
      };
    });

    const csv = Papa.unparse(rows);
    // Prepend UTF-8 BOM so Excel (especially on Windows) opens it correctly
    // instead of mangling special characters like ✓/₹ if they appear.
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const dateStamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `indus_inventory_export_${dateStamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadSampleCsv = () => {
    const sample = 'name,colors\n2225,Black; Brown\n4259,Black; Tan; Grey\n';
    const blob = new Blob([sample], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'indus_bulk_upload_sample.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const parseCsvFile = (file) => {
    if (!file) return;
    setBulkFileName(file.name);
    setBulkResults(null);
    setBulkParseErrors([]);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const errors = [];
        const grouped = {}; // name -> product

        results.data.forEach((row, idx) => {
          const rowNum = idx + 2; // account for header row
          const name = (row.name || row.articleId || '').trim();
          const colorsRaw = (row.colors || row.color || '').trim();

          if (!name) { errors.push(`Row ${rowNum}: missing "name"`); return; }
          if (!colorsRaw) { errors.push(`Row ${rowNum} ("${name}"): missing "colors"`); return; }

          const colorNames = colorsRaw.split(/[;,]/).map(c => expandColorAbbreviations(c.trim())).filter(Boolean);
          if (colorNames.length === 0) { errors.push(`Row ${rowNum} ("${name}"): no valid colors found`); return; }

          const key = name.toLowerCase();
          if (!grouped[key]) {
            grouped[key] = {
              name,
              variations: colorNames.map(color => ({ color, image: '' })),
            };
          } else {
            // Same name appearing on multiple rows — merge additional colors in.
            colorNames.forEach(color => {
              if (!grouped[key].variations.some(v => v.color.toLowerCase() === color.toLowerCase())) {
                grouped[key].variations.push({ color, image: '' });
              }
            });
          }
        });

        setBulkParsedProducts(Object.values(grouped));
        setBulkParseErrors(errors);
      },
      error: (err) => {
        setBulkParseErrors([`Failed to parse CSV: ${err.message}`]);
        setBulkParsedProducts([]);
      }
    });
  };

  const handleBulkFileSelect = (e) => {
    parseCsvFile(e.target.files?.[0]);
  };

  const [bulkCsvDragActive, setBulkCsvDragActive] = useState(false);
  const handleBulkCsvDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setBulkCsvDragActive(true);
  };
  const handleBulkCsvDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setBulkCsvDragActive(false);
  };
  const handleBulkCsvDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setBulkCsvDragActive(false);
    if (bulkUploading) return;
    const file = e.dataTransfer.files?.[0];
    if (file && file.name.toLowerCase().endsWith('.csv')) {
      parseCsvFile(file);
    } else {
      alert('Please drop a .csv file.');
    }
  };

  const handleBulkUpload = async () => {
    if (!db || bulkParsedProducts.length === 0) return;
    if (!bulkCategory) { alert('Please choose a category for this batch before uploading.'); return; }
    setBulkUploading(true);
    setBulkProgress({ done: 0, total: bulkParsedProducts.length });

    let added = 0, updated = 0, skipped = 0, skippedColors = 0, failed = 0;
    const productsByName = new Map(products.map(p => [String(p.name || '').trim().toLowerCase(), p]));

    for (let i = 0; i < bulkParsedProducts.length; i++) {
      const prod = bulkParsedProducts[i];
      const key = prod.name.trim().toLowerCase();
      const existing = productsByName.get(key);

      if (existing && bulkUploadMode === 'new_only') {
        // New-only mode: existing products are left completely alone
        skipped++;
      } else if (existing && bulkUploadMode === 'update_existing') {
        // Update-existing mode: only genuinely NEW colors get added —
        // any color this product already has is skipped entirely, never
        // touched/overwritten, same rule as Bulk Photos.
        const existingColorNames = new Set((existing.variations || []).map(v => String(v.color).trim().toLowerCase()));
        const newVariations = prod.variations.filter(v => !existingColorNames.has(String(v.color).trim().toLowerCase()));
        skippedColors += (prod.variations.length - newVariations.length);

        if (newVariations.length === 0) {
          skipped++; // every color already existed — nothing to add
        } else {
          try {
            const mergedVariations = [...(existing.variations || []), ...newVariations];
            await apiFetch(`/products/${existing.id}`, {
              method: 'PUT',
              body: JSON.stringify({ variations: mergedVariations }),
            });
            updated++;
          } catch (err) {
            console.error(`Bulk update failed for "${prod.name}":`, err);
            failed++;
          }
        }
      } else {
        // Brand new product — same in both modes
        try {
          await apiFetch('/products', {
            method: 'POST',
            body: JSON.stringify({
              name: prod.name,
              category: bulkCategory,
              audience: bulkAudience,
              articleId: prod.name,
              variations: prod.variations,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            }),
          });
          productsByName.set(key, { id: 'pending', name: prod.name, variations: prod.variations }); // guard against dupes within the same CSV
          added++;
        } catch (err) {
          console.error(`Bulk upload failed for "${prod.name}":`, err);
          failed++;
        }
      }
      setBulkProgress({ done: i + 1, total: bulkParsedProducts.length });
    }

    await refetchProducts();
    setBulkResults({ added, updated, skipped, skippedColors, failed });
    setBulkUploading(false);
    setBulkParsedProducts([]);
    setBulkParsedRemovedStack([]);
    setBulkFileName('');
  };

  const handleEditProduct = (product) => {
    setEditingProductId(product.id);
    setProductData({ name: product.name || '', category: product.category || '', audience: product.audience || '' });
    const editColors = (product.variations || []).map(v => ({
      color: v.color || '', file: null, previewUrl: '', existingUrl: v.image || ''
    }));
    setColorsList(editColors.length ? editColors : [{ color: '', file: null, previewUrl: '', existingUrl: '' }]);
    setActiveTab('add_product');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Opens the edit form for a product in a NEW browser tab instead of
  // navigating away from the current inventory view — keeps your scroll
  // position, search, and category filters intact in this tab. The new
  // tab shares the same login (localStorage) since it's the same site,
  // so no re-login needed; it reads ?edit=<id> on load (see the effect
  // near the top of the App component) and jumps straight to that
  // product's edit form.
  const openEditInNewTab = (product) => {
    const url = `${window.location.origin}${window.location.pathname}?edit=${encodeURIComponent(product.id)}`;
    window.open(url, '_blank');
  };

  // Opens a dedicated, focused preview page for one product — every
  // color shown as an actual image thumbnail with its color name label,
  // so you can visually verify every photo is correct at a glance
  // instead of scrolling through small chips. Opens in a new tab so it
  // never disturbs your current inventory view.
  const openPreviewInNewTab = (product) => {
    const url = `${window.location.origin}${window.location.pathname}?preview=${encodeURIComponent(product.id)}`;
    window.open(url, '_blank');
  };

  // ── FIX SPLIT PRODUCTS ──────────────────────────────────────────────────
  // Detects the specific mistake where bulk-photo filenames like
  // "ORTHO CARE-02 BLACK.jpg" got parsed as article="ORTHO" + a fake
  // "color" of "CARE-02/BLACK" — because CARE-02, I-10, I-20 etc. are
  // actually separate sub-articles, not real colors. This scans every
  // product's variations for a color field containing "/" (the tell-tale
  // sign), and offers to split each affected product into proper
  // separate products — one per real sub-article, each with its actual
  // color(s) — in one click.
  const [splitScanResults, setSplitScanResults] = useState(null); // null = not scanned yet
  const [splitApplying, setSplitApplying] = useState(false);
  const [splitApplyProgress, setSplitApplyProgress] = useState(null);
  const [splitResults, setSplitResults] = useState(null);

  // A color string like "CARE-02/BLACK" or "I/10/Mehandi" is a mistaken
  // sub-article merge — CARE-02 and I/10 are article codes, not colors.
  // A color string like "Black/Tan" or "N.Blue/Grey" is a GENUINE
  // dual-tone color (a real, deliberate feature) — both segments are
  // real color names, neither is an article code.
  //
  // The reliable distinguishing signal: article codes (CARE-02, I-10,
  // I-20, I/10) always contain a DIGIT. Real color names (Black, Tan,
  // N.Blue, Grey, D.Grey, R.Blue) never do. So only the non-final
  // segment(s) containing a digit indicate a real sub-article problem —
  // this is what separates the ORTHO case (flag it) from the VISION-24
  // case (leave it alone, it's correct as-is).
  const isSubArticleMerge = (colorStr) => {
    const parts = String(colorStr).split('/').map(s => s.trim()).filter(Boolean);
    if (parts.length < 2) return false;
    const nonColorParts = parts.slice(0, -1);
    return nonColorParts.some(p => /\d/.test(p));
  };

  const scanForSplitIssues = () => {
    const affected = [];
    products.forEach(product => {
      const badVariations = (product.variations || []).filter(v => isSubArticleMerge(v.color));
      const goodVariations = (product.variations || []).filter(v => !isSubArticleMerge(v.color));
      if (badVariations.length === 0) return;

      // Parse each bad "color" into {subArticle, realColor} — last "/"
      // segment is the real color, everything before it is the sub-
      // article suffix (internal slashes like "I/10" get normalized to
      // "I-10" so "I/10" and "I-10" variants consolidate into one product
      // instead of splitting further due to a punctuation inconsistency).
      const newProductGroups = {}; // subArticleSuffix -> [{color, image}]
      badVariations.forEach(v => {
        const parts = String(v.color).split('/').map(s => s.trim()).filter(Boolean);
        if (parts.length < 2) return;
        const realColor = parts[parts.length - 1];
        const subArticle = parts.slice(0, -1).join('-');
        const key = subArticle;
        if (!newProductGroups[key]) newProductGroups[key] = [];
        newProductGroups[key].push({ color: realColor, image: v.image || '' });
      });

      affected.push({
        originalProduct: product,
        goodVariations,          // stays on the original product, untouched
        newProductGroups,        // becomes new separate products
      });
    });
    setSplitScanResults(affected);
    setSplitResults(null);
  };

  const applySplitFixes = async () => {
    if (!splitScanResults || splitScanResults.length === 0) return;
    if (!window.confirm(
      `This will create new products for each sub-article found and remove the incorrectly-merged colors from ${splitScanResults.length} original product(s). This cannot be automatically undone — continue?`
    )) return;

    setSplitApplying(true);
    let productsCreated = 0, productsUpdated = 0, failed = 0;
    const totalOps = splitScanResults.reduce((sum, r) => sum + Object.keys(r.newProductGroups).length + 1, 0);
    let done = 0;

    for (const { originalProduct, goodVariations, newProductGroups } of splitScanResults) {
      // Create one new product per sub-article found
      for (const [subArticle, variations] of Object.entries(newProductGroups)) {
        try {
          const newName = `${originalProduct.name}-${subArticle}`;
          await apiFetch('/products', {
            method: 'POST',
            body: JSON.stringify({
              name: newName,
              articleId: newName,
              category: originalProduct.category,
              audience: originalProduct.audience,
              variations,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            }),
          });
          productsCreated++;
        } catch (err) {
          console.error(`Failed to create split product for ${originalProduct.name}-${subArticle}:`, err);
          failed++;
        }
        done++;
        setSplitApplyProgress({ done, total: totalOps });
      }

      // Original product: if it has some genuinely correct colors left,
      // keep it with just those. If ALL its colors were the mistaken
      // sub-article/color combos (like the ORTHO example), delete it
      // entirely instead of leaving a hollow 0-color product behind.
      try {
        if (goodVariations.length > 0) {
          await apiFetch(`/products/${originalProduct.id}`, {
            method: 'PUT',
            body: JSON.stringify({ variations: goodVariations }),
          });
        } else {
          await apiFetch(`/products/${originalProduct.id}`, { method: 'DELETE' });
        }
        productsUpdated++;
      } catch (err) {
        console.error(`Failed to clean up original product ${originalProduct.name}:`, err);
        failed++;
      }
      done++;
      setSplitApplyProgress({ done, total: totalOps });
    }

    await refetchProducts();
    setSplitApplying(false);
    setSplitApplyProgress(null);
    setSplitResults({ productsCreated, productsUpdated, failed });
    setSplitScanResults(null);
  };

  // Supports opening straight into a product's edit form via URL —
  // ?edit=<productId> — used when Edit is clicked from the inventory
  // list to open in a new tab (see openEditInNewTab below) instead of
  // navigating away from the list in the current tab. Fires once, as
  // soon as the product list has loaded and contains a matching product.
  const editParamHandled = useRef(false);
  useEffect(() => {
    if (editParamHandled.current || products.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const editId = params.get('edit');
    if (editId) {
      const match = products.find(p => p.id === editId);
      if (match) {
        handleEditProduct(match);
        editParamHandled.current = true;
      }
    }
  }, [products]);

  const handleDeleteProduct = async (id) => {
    if (!db || !window.confirm('Delete this product?')) return;
    try {
      await apiFetch(`/products/${id}`, { method: 'DELETE' });
      await refetchProducts();
    }
    catch (err) { console.error(err); }
  };

  // ── Category handlers ─────────────────────────────────────────────────────
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    if (!db) return;
    setIsSubmitting(true);
    try {
      const payload = { name: catForm.name.trim(), pdfLink: catForm.pdfLink.trim() };
      if (catForm.id) {
        await apiFetch(`/categories/${catForm.id}`, { method: 'PUT', body: JSON.stringify(payload) });
        setSuccessMsg('Category updated!');
      } else {
        await apiFetch('/categories', { method: 'POST', body: JSON.stringify(payload) });
        setSuccessMsg('Category added!');
      }
      await refetchCategories();
      setCatForm({ id: null, name: '', pdfLink: '' });
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      console.error(err); alert('Failed to save category');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditCategory   = (cat) => { setCatForm({ id: cat.id, name: cat.name, pdfLink: cat.pdfLink || '' }); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const handleDeleteCategory = async (id) => {
    if (!db) return;
    try {
      await apiFetch(`/categories/${id}`, { method: 'DELETE' });
      await refetchCategories();
    }
    catch (err) { console.error(err); }
  };

  // ── Bot Tester — posts JSON {Body, From} to the Flask server's dedicated
  //    /whatsapp-test route. That route runs the message through the exact
  //    same handler the real Meta webhook uses, but intercepts every
  //    outbound send_* call instead of hitting graph.facebook.com, and
  //    returns them as a JSON "outbox" array. No Meta credentials or
  //    public URL needed — fully local. See server.py's whatsapp_test().
  const sendTestMessage = async (overrideBody) => {
    const body = overrideBody !== undefined ? overrideBody : testInput;
    if (!body.trim() || isSendingTest) return;

    setChatLog(prev => [...prev, { sender: 'user', text: body }]);
    setTestInput('');
    setIsSendingTest(true);

    try {
      const res = await fetch(`${botEndpoint.replace(/\/$/, '')}/whatsapp-test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(botAdminKey ? { 'X-Admin-Key': botAdminKey } : {}),
        },
        body: JSON.stringify({ Body: body, From: testFromNumber }),
      });
      if (res.status === 401) {
        setChatLog(prev => [...prev, { sender: 'bot', text: '⚠️ Unauthorized (401) — check the Admin Key field below matches ADMIN_API_KEY on the server.' }]);
        setIsSendingTest(false);
        return;
      }
      const data = await res.json();
      const outbox = data.outbox || [];

      if (outbox.length === 0) {
        setChatLog(prev => [...prev, { sender: 'bot', text: '(no reply sent — check your Flask server logs)' }]);
      } else {
        for (const item of outbox) {
          if (item.type === 'text') {
            setChatLog(prev => [...prev, { sender: 'bot', text: item.body }]);
          } else if (item.type === 'media' || item.type === 'document') {
            setChatLog(prev => [...prev, { sender: 'bot', text: item.caption || '', mediaUrl: item.media_url }]);
          } else if (item.type === 'buttons') {
            const labels = (item.buttons || []).map(b => b.title).join('  |  ');
            setChatLog(prev => [...prev, { sender: 'bot', text: `${item.body}\n\n[buttons: ${labels}]` }]);
          } else if (item.type === 'list') {
            const labels = (item.rows || []).map(r => r.title).join(', ');
            setChatLog(prev => [...prev, { sender: 'bot', text: `${item.body}\n\n[list "${item.button_label}": ${labels}]` }]);
          }
        }
      }
    } catch (err) {
      setChatLog(prev => [...prev, { sender: 'bot', text: `⚠️ Request failed: ${err.message}. Check botEndpoint URL and that the server is running/CORS-enabled.` }]);
    } finally {
      setIsSendingTest(false);
    }
  };

  const resetTestSession = () => {
    setChatLog([]);
    sendTestMessage('menu');
  };

  // ── PREVIEW MODE ─────────────────────────────────────────────────────────
  // A dedicated, minimal page (no sidebar/tabs) for verifying every color
  // photo of one product at a glance — opened via the Preview (eye) button
  // on inventory cards, always in a new tab so it never disturbs your main
  // view. Every color shown as an actual thumbnail with its name, plus a
  // clear "NO IMAGE" flag for anything missing.
  const previewParams = new URLSearchParams(window.location.search);
  const previewId = previewParams.get('preview');
  if (previewId) {
    const previewProduct = products.find(p => p.id === previewId);
    if (!previewProduct) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: COLORS.secondary }} />
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-gray-50 p-6 md:p-10">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-extrabold" style={{ color: COLORS.primary }}>{previewProduct.name}</h1>
              <p className="text-sm text-gray-500 mt-1">{previewProduct.category} {previewProduct.audience ? `· ${previewProduct.audience}` : ''} · {(previewProduct.variations || []).length} color(s)</p>
            </div>
            <button onClick={() => window.close()} className="text-xs font-bold text-gray-500 border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-100">Close Tab</button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {(previewProduct.variations || []).map((v, i) => {
              const imgOk = v.image && String(v.image).startsWith('http');
              return (
                <div key={i} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  {imgOk ? (
                    <img src={v.image} alt={v.color} className="w-full aspect-square object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full aspect-square bg-red-50 border-b border-red-200 flex items-center justify-center text-red-500 text-xs font-bold">NO IMAGE</div>
                  )}
                  <div className="p-2 text-center text-sm font-bold text-gray-700">{v.color}</div>
                </div>
              );
            })}
          </div>
          {(previewProduct.variations || []).length === 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-sm text-gray-400">
              No colors on this product yet.
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex h-[calc(100vh-64px)] overflow-hidden">

      {/* ── Sidebar (desktop) ── */}
      <div className="w-64 bg-white border-r border-gray-200 hidden md:flex flex-col h-full shadow-sm z-10">
        <div className="p-4 border-b border-gray-100">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Inventory Management</p>
          <div className="space-y-1">
            {[
              { key: 'add_product', icon: <Upload className="w-4 h-4 mr-3" />, label: 'Single Upload' },
              { key: 'bulk_upload', icon: <FileText className="w-4 h-4 mr-3" />, label: 'Bulk Upload' },
              { key: 'bulk_photos', icon: <ImageIcon className="w-4 h-4 mr-3" />, label: 'Bulk Photos' },
              { key: 'inventory',   icon: <Package  className="w-4 h-4 mr-3" />, label: 'View Inventory' },
              { key: 'categories',  icon: <LayoutList className="w-4 h-4 mr-3" />, label: 'Category Master' },
              { key: 'bot_tester',  icon: <MessageSquare className="w-4 h-4 mr-3" />, label: 'Bot Tester' },
              { key: 'activity',    icon: <BarChart3 className="w-4 h-4 mr-3" />, label: 'Activity' },
              { key: 'fix_split',   icon: <AlertCircle className="w-4 h-4 mr-3" />, label: 'Fix Split Products' },
              { key: 'reports',     icon: <FileText className="w-4 h-4 mr-3" />, label: 'Reports' },
            ].map(({ key, icon, label }) => (
              <a key={key} href={`?tab=${key}`} onClick={(e) => handleNavClick(e, key)}
                className={`w-full flex items-center px-3 py-2.5 text-sm font-bold rounded-lg transition-colors ${activeTab === key ? 'bg-teal-50 text-teal-700' : 'text-gray-600 hover:bg-gray-50'}`}
                style={{ color: activeTab === key ? COLORS.secondary : undefined }}>
                {React.cloneElement(icon, { style: { color: activeTab === key ? COLORS.secondary : undefined } })}
                {label}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="flex-1 overflow-y-auto bg-gray-50 p-4 md:p-8">

        {/* Mobile tabs */}
        <div className="flex bg-white rounded-xl shadow-sm border border-gray-200 p-1 mb-6 md:hidden">
          {[['add_product','Upload'],['bulk_upload','Bulk'],['bulk_photos','Photos'],['inventory','Inventory'],['categories','Categories'],['bot_tester','Bot Test'],['activity','Activity'],['fix_split','Fix Split'],['reports','Reports']].map(([key, label]) => (
            <a key={key} href={`?tab=${key}`} onClick={(e) => handleNavClick(e, key)}
              className={`flex-1 py-2 text-xs font-bold rounded-lg text-center ${activeTab === key ? 'bg-gray-800 text-white' : 'text-gray-500'}`}>
              {label}
            </a>
          ))}
        </div>

        {/* Alerts */}
        {successMsg && (
          <div className="mb-6 p-4 bg-teal-50 text-teal-800 border-l-4 rounded-r-lg shadow-sm flex items-center font-bold" style={{ borderColor: COLORS.secondary }}>
            <CheckCircle2 className="w-5 h-5 mr-3" style={{ color: COLORS.secondary }} />{successMsg}
          </div>
        )}
        {errorMsg && (
          <div className="mb-6 p-4 bg-red-50 text-red-800 border-l-4 border-red-500 rounded-r-lg shadow-sm flex items-center font-bold">
            <AlertCircle className="w-5 h-5 mr-3 text-red-500" />{errorMsg}
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            UPLOAD TAB
        ════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'add_product' && (
          <div className="max-w-5xl mx-auto space-y-6">
            <div className="flex items-center text-sm text-gray-500 mb-2 font-bold uppercase tracking-wider">
              <span>Home</span><ChevronRight className="w-4 h-4 mx-1" /><span>Products</span><ChevronRight className="w-4 h-4 mx-1" />
              <span style={{ color: COLORS.primary }}>{editingProductId ? 'Edit Product' : 'Create'}</span>
            </div>

            <form onSubmit={handleProductSubmit} className="space-y-6">

              {/* Basic details card */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="font-bold text-gray-800">Basic Details</h3>
                  {!editingProductId && <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded font-bold">Article ID = Name</span>}
                </div>
                <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1.5">Product Name / Art No. <span className="text-red-500">*</span></label>
                    <input required type="text" value={productData.name}
                      onChange={e => setProductData({ ...productData, name: e.target.value })}
                      className={`w-full border rounded-lg p-2.5 text-sm focus:ring-2 focus:outline-none bg-white ${
                        productData.name.trim() && products.some(p => p.id !== editingProductId && String(p.name || '').trim().toLowerCase() === productData.name.trim().toLowerCase())
                          ? 'border-amber-400' : 'border-gray-300'
                      }`}
                      placeholder="e.g. 2225" />
                    {productData.name.trim() && !editingProductId && (() => {
                      const match = products.find(p => String(p.name || '').trim().toLowerCase() === productData.name.trim().toLowerCase());
                      if (!match) return null;
                      return (
                        <p className="text-[10px] text-amber-600 mt-1 font-semibold">
                          ⚠ "{match.name}" already exists ({(match.variations || []).length} color(s)) — saving will add only NEW colors to it, existing ones stay untouched.
                        </p>
                      );
                    })()}
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1.5">Category <span className="text-red-500">*</span></label>
                    <select required value={productData.category}
                      onChange={e => setProductData({ ...productData, category: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:outline-none bg-white">
                      <option value="" disabled>Select category...</option>
                      {/* If editing a product whose category was since removed from
                          Category Master, show it anyway so the value isn't silently
                          lost — just flagged as no longer an active category. */}
                      {productData.category && !dynamicCategories.includes(productData.category) && (
                        <option value={productData.category}>{productData.category} (no longer in Category Master)</option>
                      )}
                      {dynamicCategories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    {dynamicCategories.length === 0 && (
                      <p className="text-[10px] text-gray-400 mt-1">No categories yet — add one in Category Master first.</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1.5">Audience <span className="text-red-500">*</span></label>
                    <select required value={productData.audience}
                      onChange={e => setProductData({ ...productData, audience: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:outline-none bg-white">
                      <option value="">Select Audience</option>
                      {AUDIENCE_MASTERS.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Color variations card */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                  <h3 className="font-bold text-gray-800">Color / Image Variations</h3>
                </div>

                <p className="px-6 pt-4 text-xs text-gray-500">
                  WebP, PNG, JPG all accepted —
                  <span className="font-semibold text-teal-700 ml-1">auto-compressed + converted to JPEG for fastest WhatsApp delivery.</span>
                </p>

                {/* Upload progress bar */}
                {uploadProgress && (
                  <div className="mx-6 mt-4 p-3 bg-teal-50 border border-teal-200 rounded-lg">
                    <div className="flex justify-between mb-1">
                      <span className="text-xs font-bold text-teal-700">
                        Uploading image {uploadProgress.index} of {uploadProgress.total}…
                      </span>
                      <span className="text-xs font-bold text-teal-700">{uploadProgress.pct}%</span>
                    </div>
                    <div className="w-full bg-teal-100 rounded-full h-2">
                      <div className="h-2 rounded-full transition-all duration-150"
                        style={{ width: `${uploadProgress.pct}%`, backgroundColor: COLORS.secondary }} />
                    </div>
                  </div>
                )}

                <div className="p-6 space-y-4">
                  {colorsList.map((c, idx) => (
                    <div key={idx} className="flex flex-col sm:flex-row sm:items-start gap-4 pb-4 border-b border-gray-100 last:border-0 last:pb-0">
                      <div className="w-full sm:w-48 flex-shrink-0">
                        <label className="block text-[11px] font-bold text-gray-500 mb-1">Color <span className="text-red-500">*</span></label>
                        <input required list="col-list" type="text" value={c.color}
                          onChange={e => updateColorName(idx, e.target.value)}
                          className="w-full border border-gray-300 rounded-lg bg-white p-2.5 text-sm focus:ring-2 focus:outline-none"
                          placeholder="-- Select or type color --" />
                        <datalist id="col-list">{dynamicColors.map(col => <option key={col} value={col} />)}</datalist>
                      </div>
                      <div className="flex-1">
                        <label className="block text-[11px] font-bold text-gray-500 mb-1">Image <span className="text-red-500">*</span></label>
                        <div className="flex items-center gap-3">
                          <label className="cursor-pointer bg-white border border-gray-300 text-gray-700 font-medium text-xs px-4 py-2.5 rounded-lg shadow-sm hover:bg-gray-50 whitespace-nowrap">
                            {c.file ? `✓ ${c.file.name.slice(0, 20)}` : 'Choose File'}
                            <input type="file" className="sr-only" accept="image/*"
                              onChange={e => handleColorImage(e, idx)}
                              required={!c.previewUrl && !c.existingUrl} />
                          </label>
                          {(c.previewUrl || c.existingUrl) && (
                            <img src={c.previewUrl || c.existingUrl} alt="preview"
                              className="h-12 w-24 object-cover rounded-lg border border-gray-200 bg-gray-100 shadow-sm" />
                          )}
                        </div>
                        {c.file && (
                          <p className="text-[10px] text-teal-600 mt-1 font-semibold">
                            Binary JPEG upload → fastest transfer
                          </p>
                        )}
                      </div>
                      <div className="flex-shrink-0 pt-5">
                        {idx > 0 && (
                          <button type="button" onClick={() => removeColor(idx)}
                            className="w-8 h-8 flex items-center justify-center bg-red-500 text-white rounded-full hover:bg-red-600 shadow-sm"
                            title="Remove this color">
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Add More Images / Colors */}
              <div className="flex justify-start">
                <button type="button" onClick={addColor}
                  className="text-sm font-bold flex items-center px-4 py-2.5 text-white rounded-lg shadow-sm hover:opacity-90"
                  style={{ backgroundColor: COLORS.primary }}>
                  <Plus className="w-4 h-4 mr-1.5" /> Add More Images
                </button>
              </div>

              {/* Submit row */}
              <div className="flex justify-end pt-2 gap-4">
                {editingProductId && (
                  <button type="button" onClick={resetForm} disabled={isSubmitting}
                    className="px-8 py-3 bg-gray-200 text-gray-800 font-bold rounded-lg hover:bg-gray-300">
                    Cancel Edit
                  </button>
                )}
                <button type="submit" disabled={isSubmitting}
                  className="px-8 py-3 text-white font-bold rounded-lg disabled:opacity-70 flex items-center shadow-md"
                  style={{ backgroundColor: COLORS.secondary }}>
                  {isSubmitting
                    ? <><Loader2 className="w-5 h-5 animate-spin mr-2" />
                        {uploadProgress
                          ? `Uploading ${uploadProgress.index}/${uploadProgress.total} (${uploadProgress.pct}%)`
                          : 'Saving to database…'}
                      </>
                    : <><CheckCircle2 className="w-5 h-5 mr-2" />{editingProductId ? 'Update Product' : 'Save Product'}</>
                  }
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            INVENTORY TAB
        ════════════════════════════════════════════════════════════════════ */}
        {/* ════════════════════════════════════════════════════════════════════
            BULK UPLOAD TAB
        ════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'bulk_upload' && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <h2 className="text-xl font-bold mb-2" style={{ color: COLORS.primary }}>Bulk Upload (CSV)</h2>
              <p className="text-sm text-gray-500 mb-6">
                Just <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">name</code> and{' '}
                <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">colors</code> (semicolon or comma-separated, e.g. "Black; Brown; Grey") —
                no image URLs needed. Category and audience below apply to every product in this file. Add color photos afterwards from
                Inventory → Edit, whenever they're ready — nothing here waits on images.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5">Category for this batch *</label>
                  <select required value={bulkCategory} onChange={e => setBulkCategory(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:outline-none focus:ring-2 bg-white"
                    style={{ '--tw-ring-color': COLORS.secondary }}>
                    <option value="" disabled>Select category...</option>
                    {dynamicCategories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  {dynamicCategories.length === 0 && (
                    <p className="text-[10px] text-gray-400 mt-1">No categories yet — add one in Category Master first.</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5">Audience (optional)</label>
                  <select value={bulkAudience} onChange={e => setBulkAudience(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:outline-none focus:ring-2 bg-white"
                    style={{ '--tw-ring-color': COLORS.secondary }}>
                    <option value="">None</option>
                    {AUDIENCE_MASTERS.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
              </div>

              {/* Mode toggle — New Only (default, safe) vs Update Existing
                  (adds only genuinely new colors to products that already
                  exist by name; existing colors are always left untouched
                  either way, never overwritten) */}
              <div className="mb-6">
                <label className="block text-xs font-bold text-gray-600 mb-2">If an article name already exists in the database:</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setBulkUploadMode('new_only')}
                    className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-bold border-2 transition-colors ${
                      bulkUploadMode === 'new_only' ? 'text-white' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                    }`}
                    style={bulkUploadMode === 'new_only' ? { backgroundColor: COLORS.secondary, borderColor: COLORS.secondary } : {}}
                  >
                    Skip it (upload new products only)
                  </button>
                  <button
                    type="button"
                    onClick={() => setBulkUploadMode('update_existing')}
                    className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-bold border-2 transition-colors ${
                      bulkUploadMode === 'update_existing' ? 'text-white' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                    }`}
                    style={bulkUploadMode === 'update_existing' ? { backgroundColor: COLORS.secondary, borderColor: COLORS.secondary } : {}}
                  >
                    Add new colors to it
                  </button>
                </div>
                <p className="text-[11px] text-gray-400 mt-1.5">
                  {bulkUploadMode === 'update_existing'
                    ? "Only colors this product doesn't already have will be added. Colors it already has are always left completely untouched — never overwritten."
                    : 'Existing products are left completely untouched — only brand-new article names get uploaded.'}
                </p>
              </div>

              <button onClick={downloadSampleCsv} type="button"
                className="text-xs font-bold flex items-center gap-1.5 mb-4 hover:underline" style={{ color: COLORS.secondary }}>
                <FileText className="w-3.5 h-3.5" /> Download sample CSV template
              </button>

              <label
                onDragOver={handleBulkCsvDragOver}
                onDragLeave={handleBulkCsvDragLeave}
                onDrop={handleBulkCsvDrop}
                className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl py-10 cursor-pointer transition-colors ${
                  bulkCsvDragActive ? 'border-teal-500 bg-teal-50' : 'border-gray-300 hover:border-teal-400 hover:bg-teal-50/30'
                }`}>
                <Upload className={`w-8 h-8 mb-2 ${bulkCsvDragActive ? 'text-teal-500' : 'text-gray-400'}`} />
                <span className="text-sm font-bold text-gray-600">
                  {bulkCsvDragActive ? 'Drop your CSV file here' : (bulkFileName || 'Click to select, or drag & drop a CSV file')}
                </span>
                <input type="file" accept=".csv" onChange={handleBulkFileSelect} className="hidden" disabled={bulkUploading} />
              </label>

              {bulkParseErrors.length > 0 && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-red-700 font-bold text-sm mb-2">
                    <AlertCircle className="w-4 h-4" /> {bulkParseErrors.length} row(s) skipped
                  </div>
                  <ul className="text-xs text-red-600 space-y-1 max-h-40 overflow-y-auto">
                    {bulkParseErrors.map((e, i) => <li key={i}>• {e}</li>)}
                  </ul>
                </div>
              )}

              {bulkParsedProducts.length > 0 && !bulkUploading && (
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-bold text-sm" style={{ color: COLORS.primary }}>
                      Preview — {bulkParsedProducts.length} product(s) ready · category: <span style={{ color: COLORS.secondary }}>{bulkCategory || '(not set)'}</span>
                    </h3>
                    <div className="flex items-center gap-2">
                      {bulkParsedRemovedStack.length > 0 && (
                        <button
                          onClick={() => {
                            const last = bulkParsedRemovedStack[bulkParsedRemovedStack.length - 1];
                            setBulkParsedProducts(prev => [...prev, last]);
                            setBulkParsedRemovedStack(prev => prev.slice(0, -1));
                          }}
                          className="text-xs font-bold text-blue-600 hover:underline"
                        >
                          ↩ Undo last removal
                        </button>
                      )}
                      <button
                        onClick={() => {
                          if (window.confirm('Clear the entire batch? This removes all parsed products from this upload.')) {
                            setBulkParsedProducts([]);
                            setBulkParsedRemovedStack([]);
                          }
                        }}
                        className="text-xs font-bold text-red-500 hover:underline"
                      >
                        Clear All
                      </button>
                    </div>
                  </div>
                  {(() => {
                    const rowsWithStatus = bulkParsedProducts.map((p, i) => {
                      const existingProduct = products.find(existing => String(existing.name || '').trim().toLowerCase() === p.name.trim().toLowerCase());
                      let statusLabel = null;
                      let isActionable = true;

                      if (existingProduct && bulkUploadMode === 'new_only') {
                        statusLabel = <span className="text-[10px] font-bold text-orange-600 bg-orange-50 px-2 py-1 rounded flex-shrink-0 ml-2">Will skip (already exists)</span>;
                        isActionable = false;
                      } else if (existingProduct && bulkUploadMode === 'update_existing') {
                        const existingColorNames = new Set((existingProduct.variations || []).map(v => String(v.color).trim().toLowerCase()));
                        const newCount = p.variations.filter(v => !existingColorNames.has(String(v.color).trim().toLowerCase())).length;
                        if (newCount > 0) {
                          statusLabel = <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded flex-shrink-0 ml-2">Will add {newCount} new color(s)</span>;
                        } else {
                          statusLabel = <span className="text-[10px] font-bold text-gray-500 bg-gray-100 px-2 py-1 rounded flex-shrink-0 ml-2">All colors already exist</span>;
                          isActionable = false;
                        }
                      }
                      return { p, i, statusLabel, isActionable };
                    });

                    // Only show articles where something would actually
                    // happen (new product, or new colors to add) —
                    // nothing-would-happen rows are hidden automatically
                    // instead of needing to be removed one by one.
                    const actionableRows = rowsWithStatus.filter(r => r.isActionable);
                    const skippedCount = rowsWithStatus.length - actionableRows.length;

                    return (
                      <>
                        <div className="border border-gray-200 rounded-lg divide-y max-h-72 overflow-y-auto mb-2">
                          {actionableRows.map(({ p, i, statusLabel }) => (
                            <div key={i} className="p-3 flex items-center justify-between text-sm group">
                              <div>
                                <span className="font-bold">{p.name}</span>
                                <span className="text-gray-400 ml-2">{p.variations.map(v => v.color).join(', ')}</span>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                {statusLabel}
                                <button
                                  onClick={() => {
                                    setBulkParsedRemovedStack(prev => [...prev, p]);
                                    setBulkParsedProducts(prev => prev.filter((_, idx) => idx !== i));
                                  }}
                                  className="text-gray-300 hover:text-red-500 transition-colors"
                                  title={`Remove ${p.name} from this batch`}
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                        {skippedCount > 0 && (
                          <p className="text-xs text-gray-400 mb-2">
                            ⏭ {skippedCount} article(s) already fully up to date — automatically hidden from this preview, nothing will happen for them.
                          </p>
                        )}
                        {actionableRows.length === 0 && (
                          <p className="text-xs text-gray-400 mb-2">Nothing to upload — every article in this file already exists with all its colors.</p>
                        )}
                      </>
                    );
                  })()}
                  <button
                    onClick={handleBulkUpload}
                    disabled={!bulkCategory}
                    className="w-full py-3 rounded-lg text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{ backgroundColor: COLORS.secondary }}
                  >
                    <CheckCircle2 className="w-4 h-4" /> Upload {bulkParsedProducts.length} Product(s)
                  </button>
                  {!bulkCategory && <p className="text-xs text-red-600 font-semibold mt-2 text-center">Choose a category above first.</p>}
                </div>
              )}

              {bulkUploading && bulkProgress && (
                <div className="mt-6 text-center">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" style={{ color: COLORS.secondary }} />
                  <p className="text-sm font-bold text-gray-600">Uploading {bulkProgress.done} / {bulkProgress.total}...</p>
                </div>
              )}

              {bulkResults && (
                <div className="mt-6 bg-teal-50 border border-teal-200 rounded-lg p-4 text-sm">
                  <p className="font-bold" style={{ color: COLORS.primary }}>Bulk upload complete</p>
                  <p className="text-gray-600 mt-1">
                    ✅ {bulkResults.added} new product(s) added
                    {bulkResults.updated > 0 && ` · 🔄 ${bulkResults.updated} existing product(s) got new colors`}
                    {bulkResults.skipped > 0 && ` · ⏭ ${bulkResults.skipped} product(s) skipped`}
                    {bulkResults.skippedColors > 0 && ` · ${bulkResults.skippedColors} color(s) skipped (already existed)`}
                    {bulkResults.failed > 0 && ` · ❌ ${bulkResults.failed} failed`}
                  </p>
                  {bulkResults.added > 0 && (
                    <p className="text-gray-500 mt-2 text-xs">
                      New products were added without images. Go to <b>View Inventory</b> — cards show which colors still need photos (orange ⚠ chips) — click Edit to add them.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            BULK PHOTOS TAB — filename-driven, fastest path (name+color+image
            in one shot, no CSV, no manual typing)
        ════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'bulk_photos' && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <h2 className="text-xl font-bold mb-2" style={{ color: COLORS.primary }}>Bulk Photos — Fastest Way In</h2>
              <p className="text-sm text-gray-500 mb-6">
                Select a whole folder of photos at once. Name each file <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">ARTICLE COLOR.jpg</code> —
                e.g. <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">16501 BLACK.jpg</code>, <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">44107 BLACK RED.jpg</code>.
                Everything before the first space is the article/product name; everything after is the color (can have spaces, e.g. "BLACK RED").
                Same article across multiple files becomes one product with multiple color variations — all uploaded and saved in a single pass, no CSV needed at all.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5">Category for this batch *</label>
                  <select required value={bulkPhotoCategory} onChange={e => setBulkPhotoCategory(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:outline-none focus:ring-2 bg-white"
                    style={{ '--tw-ring-color': COLORS.secondary }}>
                    <option value="" disabled>Select category...</option>
                    {dynamicCategories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <p className="text-[11px] text-gray-400 mt-1">Only applied to newly created products — existing products keep their current category.</p>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5">Audience (optional)</label>
                  <select value={bulkPhotoAudience} onChange={e => setBulkPhotoAudience(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:outline-none focus:ring-2 bg-white"
                    style={{ '--tw-ring-color': COLORS.secondary }}>
                    <option value="">None</option>
                    {AUDIENCE_MASTERS.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
              </div>

              <label
                onDragOver={handleBulkPhotoDragOver}
                onDragLeave={handleBulkPhotoDragLeave}
                onDrop={handleBulkPhotoDrop}
                className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl py-10 cursor-pointer transition-colors ${
                  bulkPhotoDragActive ? 'border-teal-500 bg-teal-50' : 'border-gray-300 hover:border-teal-400 hover:bg-teal-50/30'
                }`}>
                <ImageIcon className={`w-8 h-8 mb-2 ${bulkPhotoDragActive ? 'text-teal-500' : 'text-gray-400'}`} />
                <span className="text-sm font-bold text-gray-600">
                  {bulkPhotoDragActive
                    ? 'Drop your photos here'
                    : bulkPhotoFiles.length > 0
                      ? `${bulkPhotoFiles.length} file(s) selected`
                      : 'Click to select, or drag & drop all photos at once (multi-select supported)'}
                </span>
                <input type="file" accept="image/*" multiple onChange={handleBulkPhotoFileSelect} className="hidden" disabled={bulkPhotoProcessing} />
              </label>

              {bulkPhotoFiles.some(f => f.error) && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-red-700 font-bold text-sm mb-2">
                    <AlertCircle className="w-4 h-4" /> {bulkPhotoFiles.filter(f => f.error).length} file(s) couldn't be parsed
                  </div>
                  <ul className="text-xs text-red-600 space-y-1 max-h-32 overflow-y-auto">
                    {bulkPhotoFiles.filter(f => f.error).map((f, i) => <li key={i}>• {f.error}</li>)}
                  </ul>
                </div>
              )}

              {bulkPhotoFiles.length > 0 && !bulkPhotoProcessing && (() => {
                const grouped = bulkPhotoFiles.filter(f => !f.error).reduce((acc, f) => {
                  (acc[f.article] = acc[f.article] || []).push(f.color);
                  return acc;
                }, {});

                // For each grouped article, figure out: is this a brand
                // new product, or an existing one? And for existing ones,
                // which of the uploaded colors are genuinely NEW (not on
                // the product yet) vs which will OVERWRITE an existing
                // color's photo — shown as distinct highlighted badges so
                // it's obvious at a glance before you commit the upload.
                const rows = Object.entries(grouped).map(([article, colors]) => {
                  const existingProduct = products.find(p => String(p.name || '').trim().toLowerCase() === article.toLowerCase());
                  const existingColorNames = new Set((existingProduct?.variations || []).map(v => String(v.color).trim().toLowerCase()));
                  const colorStatuses = colors.map(color => ({
                    color,
                    isNew: !existingColorNames.has(color.trim().toLowerCase()),
                  }));
                  return { article, colorStatuses, isExisting: !!existingProduct };
                });

                const newProductRows = rows.filter(r => !r.isExisting);
                // Existing products where EVERY color already exists —
                // nothing would actually happen for these on upload, so
                // hide them from the preview entirely instead of making
                // you scroll past/mentally skip "already exists" rows —
                // only genuinely actionable items are shown.
                const existingProductRows = rows.filter(r => r.isExisting && r.colorStatuses.some(cs => cs.isNew));
                const fullyUpToDateCount = rows.filter(r => r.isExisting && !r.colorStatuses.some(cs => cs.isNew)).length;

                const renderRow = ({ article, colorStatuses }) => (
                  <div key={article} className="p-3 flex items-center justify-between text-sm group">
                    <div className="flex-1">
                      <span className="font-bold">{article}</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {colorStatuses.map(({ color, isNew }, i) => (
                          <span key={i} className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                            isNew ? 'bg-teal-50 text-teal-700' : 'bg-gray-100 text-gray-500'
                          }`} title={isNew ? 'New color — will be added' : 'Already exists on this product — this file will be skipped, existing photo stays untouched'}>
                            {color} {isNew ? '· WILL ADD' : '· SKIP (exists)'}
                          </span>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        const removedGroup = bulkPhotoFiles.filter(f => !f.error && f.article === article);
                        setBulkPhotoRemovedStack(prev => [...prev, removedGroup]);
                        setBulkPhotoFiles(prev => prev.filter(f => f.error || f.article !== article));
                      }}
                      className="text-gray-300 hover:text-red-500 transition-colors flex-shrink-0 ml-2"
                      title={`Remove ${article} from this batch`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                );

                return (
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold text-sm" style={{ color: COLORS.primary }}>
                        Preview — {bulkPhotoFiles.filter(f => !f.error).length} photo(s) ready · category: <span style={{ color: COLORS.secondary }}>{bulkPhotoCategory || '(not set)'}</span>
                      </h3>
                      <div className="flex items-center gap-2">
                        {bulkPhotoRemovedStack.length > 0 && (
                          <button
                            onClick={() => {
                              const lastRemoved = bulkPhotoRemovedStack[bulkPhotoRemovedStack.length - 1];
                              setBulkPhotoFiles(prev => [...prev, ...lastRemoved]);
                              setBulkPhotoRemovedStack(prev => prev.slice(0, -1));
                            }}
                            className="text-xs font-bold text-blue-600 hover:underline"
                          >
                            ↩ Undo last removal
                          </button>
                        )}
                        <button
                          onClick={() => {
                            if (window.confirm('Clear the entire batch? This removes all selected files from this upload.')) {
                              setBulkPhotoFiles([]);
                              setBulkPhotoRemovedStack([]);
                            }
                          }}
                          className="text-xs font-bold text-red-500 hover:underline"
                        >
                          Clear All
                        </button>
                      </div>
                    </div>

                    {newProductRows.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs font-bold text-teal-700 uppercase mb-1.5">🆕 New Products ({newProductRows.length})</p>
                        <div className="border border-teal-200 rounded-lg divide-y max-h-56 overflow-y-auto">
                          {newProductRows.map(renderRow)}
                        </div>
                      </div>
                    )}

                    {existingProductRows.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs font-bold text-blue-700 uppercase mb-1.5">🔄 Existing Products — Adding/Updating Colors ({existingProductRows.length})</p>
                        <div className="border border-blue-200 rounded-lg divide-y max-h-56 overflow-y-auto">
                          {existingProductRows.map(renderRow)}
                        </div>
                      </div>
                    )}

                    {fullyUpToDateCount > 0 && (
                      <p className="text-xs text-gray-400 mb-4">
                        ⏭ {fullyUpToDateCount} article(s) already fully up to date — automatically hidden from this preview, nothing will happen for them.
                      </p>
                    )}

                    <button
                      onClick={handleBulkPhotoUpload}
                      disabled={!bulkPhotoCategory}
                      className="w-full py-3 rounded-lg text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                      style={{ backgroundColor: COLORS.secondary }}
                    >
                      <CheckCircle2 className="w-4 h-4" /> Upload {bulkPhotoFiles.filter(f => !f.error).length} Photo(s)
                    </button>
                    {!bulkPhotoCategory && <p className="text-xs text-red-600 font-semibold mt-2 text-center">Choose a category above first.</p>}
                  </div>
                );
              })()}
              )}

              {bulkPhotoProcessing && bulkPhotoProgress && (
                <div className="mt-6 text-center">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" style={{ color: COLORS.secondary }} />
                  <p className="text-sm font-bold text-gray-600">Uploading {bulkPhotoProgress.done} / {bulkPhotoProgress.total}...</p>
                </div>
              )}

              {bulkPhotoResults && (
                <div className="mt-6 bg-teal-50 border border-teal-200 rounded-lg p-4 text-sm">
                  <p className="font-bold" style={{ color: COLORS.primary }}>Bulk photo upload complete</p>
                  <p className="text-gray-600 mt-1">
                    ✅ {bulkPhotoResults.created} new product(s) created · 🔄 {bulkPhotoResults.updated} existing product(s) got new colors added
                    {bulkPhotoResults.skippedExisting > 0 && ` · ⏭ ${bulkPhotoResults.skippedExisting} file(s) skipped (color already existed, left untouched)`}
                    {bulkPhotoResults.failed > 0 && ` · ❌ ${bulkPhotoResults.failed} failed`}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            INVENTORY TAB
        ════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'inventory' && (() => {
          const allCategories = [...new Set(products.map(p => p.category).filter(Boolean))].sort();

          const toggleCategoryFilter = (cat) => {
            setInventoryCategoryFilters(prev =>
              prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
            );
          };

          const searchLower = inventorySearch.trim().toLowerCase();

          // Same idea as the bot's "2225 black" shortcut — if the plain
          // substring search finds nothing and the query has a space,
          // try splitting it into an article part + a color hint, so
          // "2225 black" (or "1911 chr") still finds the right product
          // instead of coming up empty just because no product is
          // literally NAMED "2225 black".
          const colorMatchesHint = (color, hint) => {
            const c = String(color).trim().toLowerCase();
            if (c.startsWith(hint)) return true;
            // Fuzzy fallback for abbreviations that skip letters, like
            // "chr" -> "cherry" (not a literal prefix — cherry has an
            // "e" after the "ch"). Subsequence check: every letter of
            // the hint appears in the color, in order.
            let ci = 0;
            for (let i = 0; i < hint.length; i++) {
              ci = c.indexOf(hint[i], ci);
              if (ci === -1) return false;
              ci++;
            }
            return true;
          };

          let filteredProducts = products.filter(p => {
            const matchesCategory = inventoryCategoryFilters.length === 0 || inventoryCategoryFilters.includes(p.category);
            const matchesSearch = !searchLower ||
              (p.name || '').toLowerCase().includes(searchLower) ||
              (p.articleId || '').toLowerCase().includes(searchLower);
            return matchesCategory && matchesSearch;
          });

          if (filteredProducts.length === 0 && searchLower.includes(' ')) {
            const spaceIdx = searchLower.indexOf(' ');
            const articlePart = searchLower.slice(0, spaceIdx);
            const colorHint = searchLower.slice(spaceIdx + 1).trim();
            if (articlePart && colorHint) {
              filteredProducts = products.filter(p => {
                const matchesCategory = inventoryCategoryFilters.length === 0 || inventoryCategoryFilters.includes(p.category);
                const nameLower = (p.name || '').toLowerCase();
                const artLower = (p.articleId || '').toLowerCase();
                const articleMatches = nameLower === articlePart || artLower === articlePart || nameLower.startsWith(articlePart) || artLower.startsWith(articlePart);
                if (!articleMatches) return false;
                const hasMatchingColor = (p.variations || []).some(v => colorMatchesHint(v.color, colorHint));
                return matchesCategory && hasMatchingColor;
              });
            }
          }

          filteredProducts = filteredProducts.sort((a, b) => {
            if (inventorySort === 'name') return (a.name || '').localeCompare(b.name || '');
            const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return inventorySort === 'oldest' ? aTime - bTime : bTime - aTime;
          });

          // Only render a batch at a time — with 1500+ products, mounting
          // every single card (each with an image + buttons) at once was
          // the actual cause of slow perceived loading, not the data fetch
          // itself. "Load More" grows this in place without losing scroll
          // position or re-fetching anything.
          const visibleProducts = filteredProducts.slice(0, inventoryVisibleCount);
          const hasMoreProducts = filteredProducts.length > inventoryVisibleCount;

          const hasActiveFilters = inventoryCategoryFilters.length > 0 || searchLower;

          return (
          <div className="max-w-5xl mx-auto">
            {productsLoadError && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between gap-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-bold text-red-700">Couldn't load products</p>
                    <p className="text-xs text-red-600 mt-0.5">{productsLoadError} — this is NOT the same as having zero products, the fetch itself failed. Click Retry.</p>
                  </div>
                </div>
                <button
                  onClick={() => refetchProducts()}
                  className="flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-red-600 hover:bg-red-700"
                >
                  Retry
                </button>
              </div>
            )}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold" style={{ color: COLORS.primary }}>
                  Database Inventory ({filteredProducts.length}{hasActiveFilters ? ` of ${products.length}` : ''})
                </h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => refetchProducts()}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold border hover:bg-gray-50"
                    style={{ borderColor: COLORS.secondary, color: COLORS.secondary }}
                    title="Manually refresh — data doesn't auto-poll in the background to keep database reads low, so use this if you suspect another session made a change"
                  >
                    <RotateCcw className="w-3.5 h-3.5" /> Refresh
                  </button>
                  <button
                    onClick={() => exportInventoryToExcel(filteredProducts)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold border hover:bg-gray-50"
                    style={{ borderColor: COLORS.secondary, color: COLORS.secondary }}
                    title="Export the current filtered list (with colors) as an Excel-compatible spreadsheet, for offline cross-checking against physical stock"
                  >
                    <FileDown className="w-3.5 h-3.5" /> Export to Excel
                  </button>
                  <button
                    onClick={refreshBotCache}
                    disabled={botCacheRefreshing}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-white disabled:opacity-50"
                    style={{ backgroundColor: COLORS.secondary }}
                    title="Tells the LIVE WhatsApp bot to fetch the latest product data right now — no more terminal/curl needed"
                  >
                    <RotateCcw className={`w-3.5 h-3.5 ${botCacheRefreshing ? 'animate-spin' : ''}`} /> Refresh Bot Cache
                  </button>
                </div>
              </div>

              {/* One-time setup for the bot cache refresh button above —
                  same Bot Server URL / Admin Key fields as Bot Tester,
                  shared state so you only need to set this once per
                  session. Collapsed automatically once both are filled in. */}
              {(!botEndpoint || botEndpoint === 'http://localhost:3000' || !botAdminKey) && (
                <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs font-bold text-amber-800 mb-2">Set your LIVE bot's URL + Admin Key to enable "Refresh Bot Cache" above:</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input value={botEndpoint} onChange={e => setBotEndpoint(e.target.value)}
                      className="border border-amber-300 rounded-lg p-2 text-xs font-mono focus:outline-none focus:ring-2"
                      placeholder="https://indus-whatsapp-bot.onrender.com" />
                    <input type="password" value={botAdminKey} onChange={e => setBotAdminKey(e.target.value)}
                      className="border border-amber-300 rounded-lg p-2 text-xs font-mono focus:outline-none focus:ring-2"
                      placeholder="Bot's ADMIN_API_KEY" />
                  </div>
                </div>
              )}
              {botCacheRefreshResult && (
                <div className={`mb-4 rounded-lg p-3 text-xs font-semibold ${botCacheRefreshResult.success ? 'bg-teal-50 text-teal-800 border border-teal-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                  {botCacheRefreshResult.success
                    ? `✅ Bot cache refreshed — ${botCacheRefreshResult.count} products now cached on the live bot.`
                    : `❌ Refresh failed: ${botCacheRefreshResult.error}`}
                </div>
              )}

              {/* Select-all + bulk category reassignment — fixes miscategorized
                  products (e.g. Ladies PU items accidentally saved under Gents PU)
                  by letting you pick several products and move them all at once. */}
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  id="select-all-inventory"
                  checked={filteredProducts.length > 0 && filteredProducts.every(p => selectedProductIds.has(p.id))}
                  onChange={(e) => {
                    setSelectedProductIds(prev => {
                      const next = new Set(prev);
                      if (e.target.checked) filteredProducts.forEach(p => next.add(p.id));
                      else filteredProducts.forEach(p => next.delete(p.id));
                      return next;
                    });
                  }}
                  className="w-4 h-4 rounded"
                  style={{ accentColor: COLORS.secondary }}
                />
                <label htmlFor="select-all-inventory" className="text-xs font-bold text-gray-600 cursor-pointer">
                  Select all {hasActiveFilters ? 'filtered' : ''} ({filteredProducts.length})
                </label>
                {selectedProductIds.size > 0 && (
                  <span className="text-xs font-bold text-gray-400 ml-1">· {selectedProductIds.size} selected</span>
                )}
              </div>

              {selectedProductIds.size > 0 && (
                <div className="sticky top-0 z-10 mb-4 bg-teal-50 border border-teal-200 rounded-lg p-3 flex flex-wrap items-center gap-3">
                  <span className="text-sm font-bold" style={{ color: COLORS.primary }}>
                    {selectedProductIds.size} selected
                  </span>
                  <select
                    value={bulkCategoryValue}
                    onChange={e => setBulkCategoryValue(e.target.value)}
                    disabled={bulkCategoryUpdating}
                    className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm flex-1 min-w-[180px] focus:outline-none focus:ring-2 bg-white"
                    style={{ '--tw-ring-color': COLORS.secondary }}
                  >
                    <option value="" disabled>Move to category...</option>
                    {dynamicCategories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <button
                    onClick={handleBulkCategoryUpdate}
                    disabled={bulkCategoryUpdating || !bulkCategoryValue}
                    className="px-4 py-1.5 rounded-lg text-white text-sm font-bold disabled:opacity-50 flex items-center gap-2"
                    style={{ backgroundColor: COLORS.secondary }}
                  >
                    {bulkCategoryUpdating
                      ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {bulkCategoryProgress ? `${bulkCategoryProgress.done}/${bulkCategoryProgress.total}` : '...'}</>
                      : 'Apply'}
                  </button>
                  <button
                    onClick={() => setSelectedProductIds(new Set())}
                    disabled={bulkCategoryUpdating}
                    className="text-xs font-bold text-gray-500 hover:text-gray-700"
                  >
                    Clear selection
                  </button>
                </div>
              )}

              {/* Search + sort + multi-category filter bar */}
              <div className="mb-6 space-y-3">
                <div className="flex gap-3">
                  <div className="relative flex-1">
                    <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={inventorySearch}
                      onChange={e => setInventorySearch(e.target.value)}
                      placeholder="Search by article name or number..."
                      className="w-full pl-9 pr-9 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                      style={{ '--tw-ring-color': COLORS.secondary }}
                    />
                    {inventorySearch && (
                      <button onClick={() => setInventorySearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <select
                    value={inventorySort}
                    onChange={e => setInventorySort(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 text-sm font-semibold text-gray-600 focus:outline-none focus:ring-2 bg-white"
                    style={{ '--tw-ring-color': COLORS.secondary }}
                  >
                    <option value="newest">Newest first</option>
                    <option value="oldest">Oldest first</option>
                    <option value="name">Name A–Z</option>
                  </select>
                </div>

                {allCategories.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {allCategories.map(cat => {
                      const active = inventoryCategoryFilters.includes(cat);
                      return (
                        <button
                          key={cat}
                          onClick={() => toggleCategoryFilter(cat)}
                          className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${
                            active ? 'text-white' : 'text-gray-600 bg-white border-gray-300 hover:bg-gray-50'
                          }`}
                          style={active ? { backgroundColor: COLORS.secondary, borderColor: COLORS.secondary } : {}}
                        >
                          {cat}
                        </button>
                      );
                    })}
                    {hasActiveFilters && (
                      <button
                        onClick={() => { setInventoryCategoryFilters([]); setInventorySearch(''); }}
                        className="text-xs font-bold px-3 py-1.5 rounded-full text-red-600 hover:bg-red-50"
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                )}
              </div>

              {filteredProducts.length === 0
                ? <p className="text-gray-500 text-sm text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                    {hasActiveFilters ? 'No products match your filters.' : 'No products yet.'}
                  </p>
                : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {visibleProducts.map(product => {
                      const displayImage  = product.variations?.[0]?.image;
                      const hasValidImage = displayImage && displayImage.startsWith('http');
                      const isSelected = selectedProductIds.has(product.id);
                      return (
                        <div key={product.id} className={`border rounded-xl overflow-hidden relative group bg-white shadow-sm hover:shadow-lg transition-all ${isSelected ? 'ring-2' : 'border-gray-200'}`} style={isSelected ? { '--tw-ring-color': COLORS.secondary, borderColor: COLORS.secondary } : {}}>

                          {/* Selection checkbox */}
                          <label className={`absolute top-2 left-2 z-10 flex items-center justify-center w-6 h-6 rounded-md bg-white/90 shadow border cursor-pointer ${isSelected ? '' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleProductSelection(product.id)}
                              className="w-4 h-4"
                              style={{ accentColor: COLORS.secondary }}
                            />
                          </label>

                          {/* Action buttons */}
                          <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                            <button onClick={() => openPreviewInNewTab(product)}
                              className="p-2 bg-white text-teal-600 rounded-lg shadow-md hover:bg-teal-50" title="Preview all color photos (opens in a new tab)"><Eye className="w-4 h-4" /></button>
                            <button onClick={() => openEditInNewTab(product)}
                              className="p-2 bg-white text-blue-600 rounded-lg shadow-md hover:bg-blue-50" title="Edit (opens in a new tab)"><Pencil className="w-4 h-4" /></button>
                            <button onClick={() => handleDeleteProduct(product.id)}
                              className="p-2 bg-white text-red-600 rounded-lg shadow-md hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                          </div>

                          {/* Warning badge for old base64 images */}
                          {displayImage && !hasValidImage && (
                            <div className="absolute top-2 left-2 z-10 bg-orange-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full">
                              ⚠ Re-upload for WA
                            </div>
                          )}

                          <div className="h-56 bg-gray-50 border-b border-gray-100">
                            {displayImage
                              ? <img src={displayImage} alt={product.name} className="w-full h-full object-cover bg-gray-100" loading="lazy" />
                              : <div className="w-full h-full flex items-center justify-center"><ImageIcon className="text-gray-300 w-12 h-12" /></div>
                            }
                          </div>
                          <div className="p-5">
                            <div className="font-black text-lg truncate tracking-tight" style={{ color: COLORS.primary }}>{product.name}</div>
                            <div className="flex flex-wrap gap-2 mt-3 mb-3">
                              {product.category && <span className="text-[10px] uppercase font-bold bg-gray-100 text-gray-600 px-2 py-1 rounded">{product.category}</span>}
                              {product.audience && <span className="text-[10px] uppercase font-bold bg-gray-100 text-gray-600 px-2 py-1 rounded">{product.audience}</span>}
                            </div>
                            <div className="text-sm font-semibold text-gray-500 border-t pt-3">
                              <div className="flex items-center justify-between mb-2">
                                <span>{product.variations?.length || 0} Colors</span>
                                {hasValidImage && <span className="text-teal-600 text-[10px] font-bold">✓ WA Ready</span>}
                              </div>
                              {product.variations?.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {product.variations.map((v, i) => {
                                    const imgOk = v.image && v.image.startsWith('http');
                                    return (
                                      <span key={i}
                                        className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                                          imgOk ? 'bg-gray-50 text-gray-600 border-gray-200' : 'bg-orange-50 text-orange-600 border-orange-200'
                                        }`}
                                        title={imgOk ? 'Image ready' : 'Image missing'}
                                      >
                                        {v.color}{!imgOk && ' ⚠'}
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              }

              {hasMoreProducts && (
                <div className="flex justify-center mt-6">
                  <button
                    onClick={() => setInventoryVisibleCount(prev => prev + 60)}
                    className="px-6 py-2.5 rounded-lg text-sm font-bold border hover:bg-gray-50"
                    style={{ borderColor: COLORS.secondary, color: COLORS.secondary }}
                  >
                    Load More ({filteredProducts.length - visibleProducts.length} remaining)
                  </button>
                </div>
              )}
            </div>
          </div>
          );
        })()}

        {/* ════════════════════════════════════════════════════════════════════
            CATEGORIES TAB
        ════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'categories' && (
          <div className="max-w-5xl mx-auto space-y-6">
            <div className="flex items-center text-sm text-gray-500 mb-2 font-bold uppercase tracking-wider">
              <span>Home</span><ChevronRight className="w-4 h-4 mx-1" />
              <span style={{ color: COLORS.primary }}>Category Master</span>
            </div>
            <p className="text-sm text-gray-500 -mt-4">
              This is the single master list of categories. Only categories added here appear as suggestions when adding or bulk-uploading products —
              remove one below to stop suggesting it (existing products keep their category either way). Catalogue PDF link is optional.
            </p>

            <form onSubmit={handleCategorySubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-8">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h3 className="font-bold text-gray-800">{catForm.id ? 'Edit Category' : 'Add Category'}</h3>
                <p className="text-xs text-gray-500 font-semibold">PDF link is optional — sent instantly via WhatsApp if set.</p>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5">Category Name <span className="text-red-500">*</span></label>
                  <input required list="cat-list" value={catForm.name}
                    onChange={e => setCatForm({ ...catForm, name: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:outline-none bg-white"
                    placeholder="e.g. Men PU Slippers" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5">Catalogue PDF URL</label>
                  <input type="url" value={catForm.pdfLink}
                    onChange={e => setCatForm({ ...catForm, pdfLink: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:outline-none bg-white"
                    placeholder="https://indus-chatbot.indusfootwear.com/catalogues/gents-pu.pdf" />
                  <p className="text-[10px] text-gray-400 mt-1">
                    Use a direct file URL (uploaded to Yash Host), not a Google Drive share link —
                    Drive links are slow/unreliable when sent through WhatsApp.
                  </p>
                </div>
                <div className="md:col-span-2 flex justify-end gap-3">
                  {catForm.id && (
                    <button type="button" onClick={() => setCatForm({ id: null, name: '', pdfLink: '' })}
                      className="px-6 py-2.5 bg-gray-200 text-gray-800 font-bold rounded-lg hover:bg-gray-300">Cancel</button>
                  )}
                  <button type="submit" disabled={isSubmitting}
                    className="px-6 py-2.5 text-white font-bold rounded-lg shadow-md flex items-center disabled:opacity-70"
                    style={{ backgroundColor: COLORS.secondary }}>
                    {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                    {catForm.id ? 'Update' : 'Save Category'}
                  </button>
                </div>
              </div>
            </form>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <h3 className="font-bold text-gray-800">Category Master — Active Categories</h3>
              </div>
              <div className="p-0 overflow-x-auto">
                {categoriesData.length === 0
                  ? <p className="text-gray-500 text-sm text-center py-10">No category links yet.</p>
                  : (
                    <table className="w-full text-sm text-left border-collapse">
                      <thead className="bg-white border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wider">
                        <tr>
                          <th className="px-6 py-4 font-bold w-1/3">Category</th>
                          <th className="px-6 py-4 font-bold">Link</th>
                          <th className="px-6 py-4 font-bold text-right w-28">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {categoriesData.map(cat => (
                          <tr key={cat.id} className="bg-white hover:bg-gray-50/50">
                            <td className="px-6 py-4 font-bold text-gray-800">{cat.name}</td>
                            <td className="px-6 py-4">
                              {cat.pdfLink
                                ? <a href={cat.pdfLink} target="_blank" rel="noreferrer"
                                    className="text-blue-600 hover:underline flex items-center font-medium">
                                    <FileText className="w-4 h-4 mr-1.5" /> View Link
                                  </a>
                                : <span className="text-gray-400 italic">No link</span>}
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex justify-end gap-2">
                                <button onClick={() => handleEditCategory(cat)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"><Pencil className="w-4 h-4" /></button>
                                <button onClick={() => handleDeleteCategory(cat.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
                }
              </div>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            BOT TESTER TAB — testing only, mimics Twilio's webhook POST
        ════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'bot_tester' && (
          <div className="max-w-3xl mx-auto space-y-4">
            <div className="flex items-center text-sm text-gray-500 mb-2 font-bold uppercase tracking-wider">
              <span>Home</span><ChevronRight className="w-4 h-4 mx-1" />
              <span style={{ color: COLORS.primary }}>Bot Tester</span>
            </div>

            <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold rounded-lg px-4 py-2.5 flex items-center">
              <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
              Testing only — sends to your Flask server's /whatsapp-test route, which runs the real bot logic but intercepts outbound sends instead of calling Meta's Graph API. No WhatsApp Cloud API credentials needed locally.
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-600 mb-1.5">Bot Server URL</label>
                <input value={botEndpoint} onChange={e => setBotEndpoint(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:outline-none bg-white font-mono"
                  placeholder="http://localhost:3000" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 mb-1.5">Test "From" Number</label>
                <input value={testFromNumber} onChange={e => setTestFromNumber(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:outline-none bg-white font-mono"
                  placeholder="whatsapp:+919999999999" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 mb-1.5">Admin Key</label>
                <input type="password" value={botAdminKey} onChange={e => setBotAdminKey(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:outline-none bg-white font-mono"
                  placeholder="matches ADMIN_API_KEY on server" />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col" style={{ height: '480px' }}>
              <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center" style={{ backgroundColor: COLORS.primary }}>
                <span className="text-white font-bold text-sm flex items-center">
                  <MessageSquare className="w-4 h-4 mr-2" /> Simulated WhatsApp Chat
                </span>
                <button onClick={resetTestSession}
                  className="text-white/80 hover:text-white text-xs font-bold flex items-center bg-white/10 hover:bg-white/20 px-2.5 py-1 rounded-lg">
                  <RotateCcw className="w-3 h-3 mr-1.5" /> Reset (send "menu")
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50" style={{ backgroundImage: 'radial-gradient(circle, #e5e7eb 1px, transparent 1px)', backgroundSize: '16px 16px' }}>
                {chatLog.length === 0 && (
                  <p className="text-center text-gray-400 text-xs mt-10">Send "hi" below to start testing the bot flow.</p>
                )}
                {chatLog.map((msg, i) => (
                  <div key={i} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] rounded-xl px-3 py-2 text-sm shadow-sm whitespace-pre-wrap ${
                      msg.sender === 'user' ? 'bg-teal-600 text-white' : 'bg-white text-gray-800 border border-gray-200'
                    }`}>
                      {msg.mediaUrl && (
                        msg.mediaUrl.toLowerCase().endsWith('.pdf')
                          ? <a href={msg.mediaUrl} target="_blank" rel="noreferrer"
                              className="flex items-center text-blue-600 underline text-xs font-bold mb-1.5">
                              <FileText className="w-4 h-4 mr-1" /> PDF attachment (open)
                            </a>
                          : <img src={msg.mediaUrl} alt="media" className="rounded-lg mb-1.5 max-h-48 object-contain bg-gray-100" />
                      )}
                      {msg.text}
                    </div>
                  </div>
                ))}
                {isSendingTest && (
                  <div className="flex justify-start">
                    <div className="bg-white border border-gray-200 rounded-xl px-3 py-2 text-sm shadow-sm">
                      <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                    </div>
                  </div>
                )}
              </div>

              <div className="p-3 border-t border-gray-200 bg-white flex gap-2">
                <input value={testInput} onChange={e => setTestInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && sendTestMessage()}
                  placeholder="Type a message... e.g. hi, 2225, #, next"
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:outline-none" />
                <button onClick={() => sendTestMessage()} disabled={isSendingTest}
                  className="px-4 py-2 text-white font-bold rounded-lg shadow-md disabled:opacity-50"
                  style={{ backgroundColor: COLORS.secondary }}>
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {['hi', 'browse categories', '1', '2225', 'next', 'list', '#', '5'].map(quick => (
                <button key={quick} onClick={() => sendTestMessage(quick)}
                  className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-lg font-mono">
                  {quick}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            ACTIVITY TAB — who's using the bot, what they're searching for,
            and whether it's finding real matches. Sourced from the
            activity_log Firestore collection server.py writes to on every
            meaningful search.
        ════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'activity' && (() => {
          const RESULT_LABELS = {
            exact_match: { label: 'Found', color: 'text-teal-700 bg-teal-50' },
            series: { label: 'Series shown', color: 'text-teal-700 bg-teal-50' },
            prefix: { label: 'Prefix list shown', color: 'text-blue-700 bg-blue-50' },
            partial: { label: 'Partial matches', color: 'text-blue-700 bg-blue-50' },
            suggestion: { label: 'Suggested', color: 'text-orange-700 bg-orange-50' },
            suggestion_confirmed: { label: 'Confirmed suggestion', color: 'text-teal-700 bg-teal-50' },
            no_match: { label: 'Not found', color: 'text-red-700 bg-red-50' },
          };

          const searchLower = activitySearch.trim().toLowerCase();
          const filtered = activityLog.filter(entry => {
            const matchesFilter = activityResultFilter === 'all' || entry.result_type === activityResultFilter;
            const matchesSearch = !searchLower ||
              (entry.phone || '').toLowerCase().includes(searchLower) ||
              (entry.query || '').toLowerCase().includes(searchLower) ||
              (entry.matched_name || '').toLowerCase().includes(searchLower);
            return matchesFilter && matchesSearch;
          });

          // Top searched queries — aggregated from what's currently loaded
          const queryCounts = {};
          activityLog.forEach(entry => {
            const q = (entry.query || '').trim();
            if (!q) return;
            queryCounts[q] = (queryCounts[q] || 0) + 1;
          });
          const topQueries = Object.entries(queryCounts).sort((a, b) => b[1] - a[1]).slice(0, 8);

          const noMatchCount = activityLog.filter(e => e.result_type === 'no_match').length;
          const uniquePhones = new Set(activityLog.map(e => e.phone).filter(Boolean)).size;

          // Group by phone number — total searches per customer, their most-
          // searched product, and their last-seen time. Sorted by search
          // count descending, so the most engaged customers float to top.
          const byPhone = {};
          activityLog.forEach(entry => {
            const phone = entry.phone;
            if (!phone) return;
            if (!byPhone[phone]) {
              byPhone[phone] = { phone, count: 0, queryCounts: {}, lastSeen: null, noMatchCount: 0 };
            }
            const bucket = byPhone[phone];
            bucket.count += 1;
            if (entry.result_type === 'no_match') bucket.noMatchCount += 1;
            const q = (entry.query || '').trim();
            if (q) bucket.queryCounts[q] = (bucket.queryCounts[q] || 0) + 1;
            const ts = entry.createdAt ? new Date(entry.createdAt).getTime() : 0;
            if (!bucket.lastSeen || ts > bucket.lastSeen) bucket.lastSeen = ts;
          });

          const customerRows = Object.values(byPhone).map(c => {
            const topQuery = Object.entries(c.queryCounts).sort((a, b) => b[1] - a[1])[0];
            return {
              ...c,
              topQuery: topQuery ? topQuery[0] : '—',
              topQueryCount: topQuery ? topQuery[1] : 0,
            };
          }).sort((a, b) => b.count - a.count);

          const customerSearchLower = activitySearch.trim().toLowerCase();
          const filteredCustomers = customerRows.filter(c =>
            !customerSearchLower ||
            c.phone.toLowerCase().includes(customerSearchLower) ||
            c.topQuery.toLowerCase().includes(customerSearchLower)
          );

          // ── MISSING ARTICLES — every search that came back "Not found"
          // or only got a fuzzy "Suggested" match, grouped by the actual
          // text searched (case-insensitive), so you can see exactly what
          // customers are looking for that isn't in your catalogue yet —
          // ranked by how often it's been asked for.
          //
          // IMPORTANT accuracy fix: a search logged as "Suggested" means
          // the bot found something CLOSE but not exact — that could mean
          // a real product exists under a slightly different spelling
          // (not actually missing), or the product was added to the
          // catalogue AFTER that search happened. Either way, counting it
          // as "missing" would make this handover report inaccurate. So
          // every candidate query is cross-checked against the CURRENT
          // live product list — using the same normalization the bot
          // itself uses (strip spaces/hyphens/dots, lowercase) — and only
          // kept if it genuinely doesn't match anything that exists right
          // now.
          const normalizeForMatch = (s) => String(s || '').replace(/[\s\-_.]+/g, '').toLowerCase();
          const currentProductKeys = new Set();
          products.forEach(p => {
            if (p.name) currentProductKeys.add(normalizeForMatch(p.name));
            if (p.articleId) currentProductKeys.add(normalizeForMatch(p.articleId));
          });

          const missingByQuery = {};
          activityLog.forEach(entry => {
            if (entry.result_type !== 'no_match' && entry.result_type !== 'suggestion') return;
            const q = (entry.query || '').trim();
            if (!q) return;
            // Skip anything that actually matches a product that exists
            // right now — it's not genuinely missing, just a naming/typo
            // mismatch at the time, or added since.
            if (currentProductKeys.has(normalizeForMatch(q))) return;

            const key = q.toLowerCase();
            if (!missingByQuery[key]) {
              missingByQuery[key] = { query: q, count: 0, phones: new Set(), lastSeen: null };
            }
            const bucket = missingByQuery[key];
            bucket.count++;
            if (entry.phone) bucket.phones.add(entry.phone);
            const ts = entry.createdAt ? new Date(entry.createdAt).getTime() : 0;
            if (!bucket.lastSeen || ts > bucket.lastSeen) bucket.lastSeen = ts;
          });
          const missingRows = Object.values(missingByQuery)
            .map(b => ({ query: b.query, count: b.count, uniqueCustomers: b.phones.size, lastSeen: b.lastSeen }))
            .sort((a, b) => b.count - a.count);

          const missingSearchLower = activitySearch.trim().toLowerCase();
          const filteredMissing = missingRows.filter(m =>
            !missingSearchLower || m.query.toLowerCase().includes(missingSearchLower)
          );

          const downloadMissingReport = () => {
            const rows = [
              ['Searched Query', 'Times Searched', 'Unique Customers', 'Last Searched (IST)'],
              ...missingRows.map(m => [
                m.query, m.count, m.uniqueCustomers,
                m.lastSeen ? new Date(m.lastSeen).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '',
              ]),
            ];
            const csv = Papa.unparse(rows);
            const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `missing_articles_${new Date().toISOString().slice(0, 10)}.csv`;
            a.click();
            URL.revokeObjectURL(url);
          };

          return (
            <div className="max-w-6xl mx-auto space-y-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center text-sm text-gray-500 font-bold uppercase tracking-wider">
                  <span>Home</span><ChevronRight className="w-4 h-4 mx-1" />
                  <span style={{ color: COLORS.primary }}>Activity</span>
                </div>
                <button
                  onClick={() => setActivityRefreshKey(k => k + 1)}
                  disabled={activityLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border hover:bg-gray-50 disabled:opacity-50"
                  style={{ borderColor: COLORS.secondary, color: COLORS.secondary }}
                >
                  <RotateCcw className={`w-3.5 h-3.5 ${activityLoading ? 'animate-spin' : ''}`} /> Refresh
                </button>
              </div>

              {activityLoading && (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-6 h-6 animate-spin" style={{ color: COLORS.secondary }} />
                </div>
              )}

              {!activityLoading && activityLog.length === 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-sm text-gray-500">
                  No activity logged yet — this fills in as customers search the bot on WhatsApp.
                </div>
              )}

              {!activityLoading && activityLog.length > 0 && (
                <>
                  {/* Summary cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <p className="text-2xl font-extrabold" style={{ color: COLORS.primary }}>{activityLog.length}</p>
                      <p className="text-xs font-bold text-gray-500 uppercase mt-1">Searches (last 500)</p>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <p className="text-2xl font-extrabold" style={{ color: COLORS.primary }}>{uniquePhones}</p>
                      <p className="text-xs font-bold text-gray-500 uppercase mt-1">Unique numbers</p>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <p className="text-2xl font-extrabold text-red-600">{noMatchCount}</p>
                      <p className="text-xs font-bold text-gray-500 uppercase mt-1">Not found</p>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <p className="text-2xl font-extrabold" style={{ color: COLORS.primary }}>
                        {activityLog.length ? Math.round(((activityLog.length - noMatchCount) / activityLog.length) * 100) : 0}%
                      </p>
                      <p className="text-xs font-bold text-gray-500 uppercase mt-1">Match rate</p>
                    </div>
                  </div>

                  {/* Top searches */}
                  {topQueries.length > 0 && (
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <h3 className="text-xs font-bold text-gray-500 uppercase mb-3">Most searched</h3>
                      <div className="flex flex-wrap gap-2">
                        {topQueries.map(([q, count]) => (
                          <span key={q} className="text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-100 text-gray-700">
                            {q} <span className="text-gray-400">× {count}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* View mode toggle */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => setActivityViewMode('customers')}
                      className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                        activityViewMode === 'customers' ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      style={activityViewMode === 'customers' ? { backgroundColor: COLORS.secondary } : {}}
                    >
                      👤 By Customer ({customerRows.length})
                    </button>
                    <button
                      onClick={() => setActivityViewMode('log')}
                      className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                        activityViewMode === 'log' ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      style={activityViewMode === 'log' ? { backgroundColor: COLORS.secondary } : {}}
                    >
                      📋 Full Search Log
                    </button>
                    <button
                      onClick={() => setActivityViewMode('missing')}
                      className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                        activityViewMode === 'missing' ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      style={activityViewMode === 'missing' ? { backgroundColor: COLORS.secondary } : {}}
                    >
                      🔍 Missing Articles ({missingRows.length})
                    </button>
                  </div>

                  {/* Filters — shared search box, result filter only relevant in log view */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                      <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        value={activitySearch}
                        onChange={e => setActivitySearch(e.target.value)}
                        placeholder={
                          activityViewMode === 'customers' ? "Search by phone number or their top search..." :
                          activityViewMode === 'missing' ? "Search by article name/number..." :
                          "Search by phone number, query, or matched article..."
                        }
                        className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                        style={{ '--tw-ring-color': COLORS.secondary }}
                      />
                    </div>
                    {activityViewMode === 'log' && (
                      <select
                        value={activityResultFilter}
                        onChange={e => setActivityResultFilter(e.target.value)}
                        className="border border-gray-300 rounded-lg px-3 text-sm font-semibold text-gray-600 bg-white focus:outline-none focus:ring-2"
                        style={{ '--tw-ring-color': COLORS.secondary }}
                      >
                        <option value="all">All results</option>
                        {Object.entries(RESULT_LABELS).map(([key, { label }]) => (
                          <option key={key} value={key}>{label}</option>
                        ))}
                      </select>
                    )}
                    {activityViewMode === 'missing' && (
                      <button
                        onClick={downloadMissingReport}
                        className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-bold border hover:bg-gray-50 whitespace-nowrap"
                        style={{ borderColor: COLORS.secondary, color: COLORS.secondary }}
                      >
                        <FileDown className="w-4 h-4" /> Download Report
                      </button>
                    )}
                  </div>

                  {/* MISSING ARTICLES — what customers searched for that
                      isn't in the catalogue, ranked by how often it's
                      been asked for */}
                  {activityViewMode === 'missing' && (
                    <>
                      <p className="text-xs text-gray-400 -mt-1">
                        ✓ Cross-checked against your current catalogue — anything that now matches an existing article (even a recently added one) is automatically excluded, so this only shows what's genuinely absent right now.
                      </p>
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                              <th className="text-left px-4 py-2.5 font-bold text-gray-500 text-xs uppercase">Searched Query</th>
                              <th className="text-right px-4 py-2.5 font-bold text-gray-500 text-xs uppercase">Times Searched</th>
                              <th className="text-right px-4 py-2.5 font-bold text-gray-500 text-xs uppercase">Unique Customers</th>
                              <th className="text-right px-4 py-2.5 font-bold text-gray-500 text-xs uppercase">Last Searched</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {filteredMissing.slice(0, 200).map((m, i) => (
                              <tr key={i} className="hover:bg-gray-50">
                                <td className="px-4 py-2.5 font-bold text-gray-800">{m.query}</td>
                                <td className="px-4 py-2.5 text-right font-bold" style={{ color: COLORS.secondary }}>{m.count}</td>
                                <td className="px-4 py-2.5 text-right text-gray-600">{m.uniqueCustomers}</td>
                                <td className="px-4 py-2.5 text-right text-gray-500 whitespace-nowrap">
                                  {m.lastSeen ? new Date(m.lastSeen).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short' }) : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {filteredMissing.length === 0 && (
                        <div className="text-center text-sm text-gray-400 py-10">
                          {missingRows.length === 0 ? 'No missing articles found — every search in this batch found a match.' : 'No results match your search.'}
                        </div>
                      )}
                    </div>
                    </>
                  )}

                  {/* BY CUSTOMER — grouped view, ranked by usage/interest level */}
                  {activityViewMode === 'customers' && (
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                              <th className="text-left px-4 py-2.5 font-bold text-gray-500 text-xs uppercase">Phone</th>
                              <th className="text-left px-4 py-2.5 font-bold text-gray-500 text-xs uppercase">Searches</th>
                              <th className="text-left px-4 py-2.5 font-bold text-gray-500 text-xs uppercase">Interest Level</th>
                              <th className="text-left px-4 py-2.5 font-bold text-gray-500 text-xs uppercase">Most Searched</th>
                              <th className="text-left px-4 py-2.5 font-bold text-gray-500 text-xs uppercase">Last Active</th>
                              <th className="text-left px-4 py-2.5 font-bold text-gray-500 text-xs uppercase">Action</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {filteredCustomers.slice(0, 200).map(c => {
                              const maxCount = customerRows[0]?.count || 1;
                              const interestPct = Math.round((c.count / maxCount) * 100);
                              const interestLabel = c.count >= 10 ? 'Very High' : c.count >= 5 ? 'High' : c.count >= 2 ? 'Medium' : 'Low';
                              const interestColor = c.count >= 10 ? 'bg-teal-500' : c.count >= 5 ? 'bg-teal-400' : c.count >= 2 ? 'bg-amber-400' : 'bg-gray-300';
                              const isBlocked = blockedNumbers.has(c.phone);
                              return (
                                <tr key={c.phone} className={`hover:bg-gray-50 ${isBlocked ? 'opacity-50' : ''}`}>
                                  <td className="px-4 py-2.5 font-mono font-semibold text-gray-800 whitespace-nowrap">
                                    {c.phone}
                                    {isBlocked && <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-600">BLOCKED</span>}
                                  </td>
                                  <td className="px-4 py-2.5 text-gray-600 font-bold">{c.count}</td>
                                  <td className="px-4 py-2.5">
                                    <div className="flex items-center gap-2">
                                      <div className="w-20 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                                        <div className={`h-full rounded-full ${interestColor}`} style={{ width: `${interestPct}%` }} />
                                      </div>
                                      <span className="text-[11px] font-bold text-gray-500">{interestLabel}</span>
                                    </div>
                                  </td>
                                  <td className="px-4 py-2.5 text-gray-700">
                                    {c.topQuery} {c.topQueryCount > 1 && <span className="text-gray-400">(× {c.topQueryCount})</span>}
                                  </td>
                                  <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">
                                    {c.lastSeen ? new Date(c.lastSeen).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' }) : '—'}
                                  </td>
                                  <td className="px-4 py-2.5">
                                    <button
                                      onClick={() => toggleBlockNumber(c.phone)}
                                      className={`text-[11px] font-bold px-2.5 py-1 rounded-lg border whitespace-nowrap ${
                                        isBlocked
                                          ? 'border-teal-300 text-teal-600 hover:bg-teal-50'
                                          : 'border-red-300 text-red-600 hover:bg-red-50'
                                      }`}
                                    >
                                      {isBlocked ? 'Unblock' : 'Block'}
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {filteredCustomers.length === 0 && (
                        <div className="text-center text-sm text-gray-400 py-8">No customers match your search.</div>
                      )}
                    </div>
                  )}

                  {/* FULL SEARCH LOG — flat, one row per search */}
                  {activityViewMode === 'log' && (
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="text-left px-4 py-2.5 font-bold text-gray-500 text-xs uppercase">When</th>
                            <th className="text-left px-4 py-2.5 font-bold text-gray-500 text-xs uppercase">Phone</th>
                            <th className="text-left px-4 py-2.5 font-bold text-gray-500 text-xs uppercase">Searched</th>
                            <th className="text-left px-4 py-2.5 font-bold text-gray-500 text-xs uppercase">Result</th>
                            <th className="text-left px-4 py-2.5 font-bold text-gray-500 text-xs uppercase">Matched</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {filtered.slice(0, 200).map(entry => {
                            const rt = RESULT_LABELS[entry.result_type] || { label: entry.result_type || '—', color: 'text-gray-600 bg-gray-100' };
                            const when = entry.createdAt ? new Date(entry.createdAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Kolkata' }) : '—';
                            return (
                              <tr key={entry.id} className="hover:bg-gray-50">
                                <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">{when}</td>
                                <td className="px-4 py-2.5 font-mono text-gray-700 whitespace-nowrap">{entry.phone || '—'}</td>
                                <td className="px-4 py-2.5 font-semibold text-gray-800">{entry.query || '—'}</td>
                                <td className="px-4 py-2.5">
                                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${rt.color}`}>{rt.label}</span>
                                </td>
                                <td className="px-4 py-2.5 text-gray-500">{entry.matched_name || (entry.match_count ? `${entry.match_count} results` : '—')}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {filtered.length === 0 && (
                      <div className="text-center text-sm text-gray-400 py-8">No results match your filters.</div>
                    )}
                    {filtered.length > 200 && (
                      <div className="text-center text-xs text-gray-400 py-3 border-t border-gray-100">
                        Showing first 200 of {filtered.length} matching entries — narrow your search to see more specific results.
                      </div>
                    )}
                  </div>
                  )}
                </>
              )}
            </div>
          );
        })()}

        {/* ════════════════════════════════════════════════════════════════════
            FIX SPLIT PRODUCTS TAB — one-click detection and fix for the
            "sub-article mistaken for color" bulk-upload naming mistake.
        ════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'fix_split' && (
          <div className="max-w-5xl mx-auto space-y-4">
            <div className="flex items-center text-sm text-gray-500 mb-2 font-bold uppercase tracking-wider">
              <span>Home</span><ChevronRight className="w-4 h-4 mx-1" />
              <span style={{ color: COLORS.primary }}>Fix Split Products</span>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-bold mb-2" style={{ color: COLORS.primary }}>Detect & Fix Merged Sub-Articles</h2>
              <p className="text-sm text-gray-500 mb-4">
                Finds products where a bulk-photo filename like <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">ORTHO CARE-02 BLACK.jpg</code> got
                parsed as one product ("ORTHO") with a fake color ("CARE-02/BLACK") — when <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">CARE-02</code> is
                actually a separate sub-article, not a color. This splits each one into proper, separate products automatically.
              </p>

              <button
                onClick={scanForSplitIssues}
                className="px-4 py-2.5 rounded-lg text-white font-bold text-sm flex items-center gap-2"
                style={{ backgroundColor: COLORS.secondary }}
              >
                <Search className="w-4 h-4" /> Scan Catalogue
              </button>

              {splitScanResults && splitScanResults.length === 0 && (
                <div className="mt-6 bg-teal-50 border border-teal-200 rounded-lg p-4 text-sm text-teal-800">
                  ✅ No merged sub-article issues found — your catalogue looks clean.
                </div>
              )}

              {splitScanResults && splitScanResults.length > 0 && !splitApplying && (
                <div className="mt-6">
                  <h3 className="font-bold text-sm mb-3" style={{ color: COLORS.primary }}>
                    Found {splitScanResults.length} affected product(s):
                  </h3>
                  <div className="border border-gray-200 rounded-lg divide-y max-h-96 overflow-y-auto mb-4">
                    {splitScanResults.map(({ originalProduct, goodVariations, newProductGroups }) => (
                      <div key={originalProduct.id} className="p-4 group">
                        <div className="flex items-start justify-between">
                          <div className="font-bold text-sm mb-2">
                            {originalProduct.name}
                            <span className="text-gray-400 font-normal ml-2">
                              → will become {Object.keys(newProductGroups).length} new product(s)
                              {goodVariations.length > 0 ? `, keeping ${goodVariations.length} correct color(s)` : ', and be removed (no correct colors left)'}
                            </span>
                          </div>
                          <button
                            onClick={() => setSplitScanResults(prev => prev.filter(r => r.originalProduct.id !== originalProduct.id))}
                            className="text-gray-300 hover:text-red-500 transition-colors flex-shrink-0 ml-2"
                            title={`Don't fix ${originalProduct.name} — leave it exactly as-is`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {Object.entries(newProductGroups).map(([subArticle, variations]) => (
                            <span key={subArticle} className="text-[11px] font-bold px-2 py-1 rounded-full bg-blue-50 text-blue-700">
                              {originalProduct.name}-{subArticle} ({variations.map(v => v.color).join(', ')})
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={applySplitFixes}
                    className="w-full py-3 rounded-lg text-white font-bold flex items-center justify-center gap-2"
                    style={{ backgroundColor: COLORS.secondary }}
                  >
                    <CheckCircle2 className="w-4 h-4" /> Apply Fix — Split {splitScanResults.length} Product(s)
                  </button>
                </div>
              )}

              {splitApplying && splitApplyProgress && (
                <div className="mt-6 text-center">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" style={{ color: COLORS.secondary }} />
                  <p className="text-sm font-bold text-gray-600">Applying fixes {splitApplyProgress.done} / {splitApplyProgress.total}...</p>
                </div>
              )}

              {splitResults && (
                <div className="mt-6 bg-teal-50 border border-teal-200 rounded-lg p-4 text-sm">
                  <p className="font-bold" style={{ color: COLORS.primary }}>Fix applied</p>
                  <p className="text-gray-600 mt-1">
                    ✅ {splitResults.productsCreated} new product(s) created · 🔄 {splitResults.productsUpdated} original product(s) cleaned up
                    {splitResults.failed > 0 && ` · ❌ ${splitResults.failed} failed`}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            REPORTS TAB — date-wise upload list. New products grouped by
            their actual createdAt date (fully accurate). New colors on
            existing products detected via snapshot-diff (accurate — exact
            colors, not a guess) and grouped by that product's updatedAt
            date (best available signal for when the edit happened, since
            colors don't have individual per-add timestamps without a
            deeper change-log). No database change or Worker redeploy
            needed — snapshot lives in this browser's localStorage.
        ════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'reports' && (() => {
          const snapshotProductsById = new Map((reportSnapshot?.products || []).map(p => [p.id, p]));

          const newProducts = [];
          const productsWithNewColors = []; // { product, newColors }

          products.forEach(p => {
            const prev = snapshotProductsById.get(p.id);
            const currentColors = (p.variations || []).map(v => v.color);
            if (!prev) {
              newProducts.push(p);
            } else {
              const prevColorSet = new Set(prev.colors.map(c => String(c).trim().toLowerCase()));
              const newColors = currentColors.filter(c => !prevColorSet.has(String(c).trim().toLowerCase()));
              if (newColors.length > 0) {
                productsWithNewColors.push({ product: p, newColors });
              }
            }
          });

          const dateKey = (isoString) => {
            if (!isoString) return 'Unknown date';
            return new Date(isoString).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', year: 'numeric' });
          };

          // Group into one structure per date: { newProducts: [...], newColorEntries: [...] }
          const byDate = {};
          newProducts.forEach(p => {
            const key = dateKey(p.createdAt);
            if (!byDate[key]) byDate[key] = { newProducts: [], newColorEntries: [] };
            byDate[key].newProducts.push(p);
          });
          productsWithNewColors.forEach(entry => {
            const key = dateKey(entry.product.updatedAt);
            if (!byDate[key]) byDate[key] = { newProducts: [], newColorEntries: [] };
            byDate[key].newColorEntries.push(entry);
          });

          // Sort dates newest first (parse back from the display string via original timestamps)
          const dateSortValue = (key) => {
            for (const p of newProducts) if (dateKey(p.createdAt) === key) return new Date(p.createdAt).getTime();
            for (const e of productsWithNewColors) if (dateKey(e.product.updatedAt) === key) return new Date(e.product.updatedAt).getTime();
            return 0;
          };
          const allSortedDates = Object.keys(byDate).sort((a, b) => dateSortValue(b) - dateSortValue(a));

          // "Show last N day(s)" cutoff — computed in IST so day
          // boundaries match what's actually displayed (midnight IST,
          // not midnight in whatever timezone the browser happens to be
          // in). reportDayRange=2 means "today and yesterday" in IST.
          const nowIstStr = new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' });
          const nowIst = new Date(nowIstStr);
          const todayIstMidnight = new Date(nowIst.getFullYear(), nowIst.getMonth(), nowIst.getDate());
          const cutoffTimestamp = todayIstMidnight.getTime() - (reportDayRange - 1) * 86400000;

          const sortedDates = reportDayRange > 0
            ? allSortedDates.filter(d => dateSortValue(d) >= cutoffTimestamp)
            : allSortedDates;

          // Totals/category breakdown reflect only what's in the selected
          // date range, not the full snapshot-diff history.
          const newProducts_inRange = sortedDates.flatMap(d => byDate[d].newProducts);
          const newColorEntries_inRange = sortedDates.flatMap(d => byDate[d].newColorEntries);

          const totalNewProducts = newProducts_inRange.length;
          const totalNewColors = newColorEntries_inRange.reduce((sum, e) => sum + e.newColors.length, 0);

          const byCategory = {};
          newProducts_inRange.forEach(p => {
            const cat = p.category || '(no category)';
            if (!byCategory[cat]) byCategory[cat] = { newProducts: 0, newColors: 0 };
            byCategory[cat].newProducts++;
          });
          newColorEntries_inRange.forEach(({ product, newColors }) => {
            const cat = product.category || '(no category)';
            if (!byCategory[cat]) byCategory[cat] = { newProducts: 0, newColors: 0 };
            byCategory[cat].newColors += newColors.length;
          });
          const categoryRows = Object.entries(byCategory).sort((a, b) =>
            (b[1].newProducts + b[1].newColors) - (a[1].newProducts + a[1].newColors)
          );

          const downloadReport = () => {
            const rows = [['Date', 'Type', 'Article/Name', 'Category', 'Audience', 'New Color(s)', 'All Current Colors']];
            sortedDates.forEach(date => {
              byDate[date].newProducts.forEach(p => rows.push([
                date, 'New Product', p.name || '', p.category || '', p.audience || '',
                (p.variations || []).map(v => v.color).join('; '),
                (p.variations || []).map(v => v.color).join('; '),
              ]));
              byDate[date].newColorEntries.forEach(({ product, newColors }) => rows.push([
                date, 'New Colors Added', product.name || '', product.category || '', product.audience || '',
                newColors.join('; '),
                (product.variations || []).map(v => v.color).join('; '),
              ]));
            });
            const csv = Papa.unparse(rows);
            const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `indus_report_${new Date().toISOString().slice(0, 10)}.csv`;
            a.click();
            URL.revokeObjectURL(url);
          };

          return (
            <div className="max-w-5xl mx-auto space-y-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center text-sm text-gray-500 font-bold uppercase tracking-wider">
                  <span>Home</span><ChevronRight className="w-4 h-4 mx-1" />
                  <span style={{ color: COLORS.primary }}>Reports</span>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={reportDayRange}
                    onChange={e => setReportDayRange(Number(e.target.value))}
                    className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs font-bold text-gray-600 bg-white focus:outline-none focus:ring-2"
                    style={{ '--tw-ring-color': COLORS.secondary }}
                  >
                    <option value={1}>Today only</option>
                    <option value={2}>Last 2 days</option>
                    <option value={3}>Last 3 days</option>
                    <option value={7}>Last 7 days</option>
                    <option value={0}>Everything since baseline</option>
                  </select>
                  <button
                    onClick={downloadReport}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border hover:bg-gray-50"
                    style={{ borderColor: COLORS.secondary, color: COLORS.secondary }}
                  >
                    <FileDown className="w-3.5 h-3.5" /> Download Report
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm('Save current catalogue as the new baseline? Future reports will compare against THIS point in time — do this after you\'ve sent the current report to your seniors.')) saveReportSnapshot();
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white"
                    style={{ backgroundColor: COLORS.secondary }}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> Save Snapshot as New Baseline
                  </button>
                </div>
              </div>

              {!reportSnapshot ? (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm font-semibold rounded-lg px-4 py-4 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>No baseline saved yet — everything currently in your catalogue shows as "new" below. Click <b>"Save Snapshot as New Baseline"</b> now to start tracking from this point forward.</span>
                </div>
              ) : (
                <p className="text-xs text-gray-500">Comparing against baseline saved <b>{new Date(reportSnapshot.savedAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short' })}</b>. New-product dates are exact; new-color dates reflect that product's last edit.</p>
              )}

              {/* Summary cards */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <p className="text-3xl font-extrabold" style={{ color: COLORS.primary }}>{totalNewProducts}</p>
                  <p className="text-xs font-bold text-gray-500 uppercase mt-1">New Products</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <p className="text-3xl font-extrabold" style={{ color: COLORS.secondary }}>{totalNewColors}</p>
                  <p className="text-xs font-bold text-gray-500 uppercase mt-1">New Colors Added</p>
                </div>
              </div>

              {/* Category breakdown */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200">
                  <h3 className="text-xs font-bold text-gray-500 uppercase">By Category</h3>
                </div>
                {categoryRows.length === 0 ? (
                  <div className="text-center text-sm text-gray-400 py-10">No changes since your last saved baseline.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-4 py-2.5 font-bold text-gray-500 text-xs uppercase">Category</th>
                        <th className="text-right px-4 py-2.5 font-bold text-gray-500 text-xs uppercase">New Products</th>
                        <th className="text-right px-4 py-2.5 font-bold text-gray-500 text-xs uppercase">New Colors</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {categoryRows.map(([category, counts]) => (
                        <tr key={category} className="hover:bg-gray-50">
                          <td className="px-4 py-2.5 font-semibold text-gray-800">{category}</td>
                          <td className="px-4 py-2.5 text-right font-bold" style={{ color: COLORS.primary }}>{counts.newProducts}</td>
                          <td className="px-4 py-2.5 text-right font-bold" style={{ color: COLORS.secondary }}>{counts.newColors}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                      <tr>
                        <td className="px-4 py-2.5 font-bold text-gray-800">Total</td>
                        <td className="px-4 py-2.5 text-right font-extrabold" style={{ color: COLORS.primary }}>{totalNewProducts}</td>
                        <td className="px-4 py-2.5 text-right font-extrabold" style={{ color: COLORS.secondary }}>{totalNewColors}</td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>

              {/* Date-wise breakdown — exactly which product, exactly which
                  colors, grouped under the date it happened */}
              {sortedDates.map(date => {
                const { newProducts: dp, newColorEntries: dc } = byDate[date];
                if (dp.length === 0 && dc.length === 0) return null;
                return (
                  <div key={date} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="bg-gray-800 px-4 py-2.5">
                      <h3 className="text-xs font-bold text-white uppercase">{date} — {dp.length} new product(s), {dc.reduce((s, e) => s + e.newColors.length, 0)} new color(s)</h3>
                    </div>
                    {dp.length > 0 && (
                      <div className="divide-y divide-gray-100">
                        {dp.map(p => (
                          <div key={p.id} className="px-4 py-2.5 text-sm flex items-start gap-2">
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 flex-shrink-0 mt-0.5">NEW</span>
                            <div>
                              <span className="font-bold">{p.name}</span>
                              <span className="text-gray-400 ml-2">({p.category})</span>
                              <div className="text-xs text-gray-500 mt-0.5">{(p.variations || []).map(v => v.color).join(', ')}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {dc.length > 0 && (
                      <div className="divide-y divide-gray-100">
                        {dc.map(({ product, newColors }) => (
                          <div key={product.id} className="px-4 py-2.5 text-sm flex items-start gap-2">
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 flex-shrink-0 mt-0.5">COLOR</span>
                            <div>
                              <span className="font-bold">{product.name}</span>
                              <span className="text-gray-400 ml-2">({product.category})</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {newColors.map((c, i) => (
                                  <span key={i} className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{c}</span>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}

      </div>
    </div>
  );
}
